/*#
# widgets3d

A small collection of **SVG-native** UI widgets with element-creator ergonomics
(built on tosijs's `svgElements` proxy, the same way [[virtual-gamepad|gamepadSvg]] is). They
render to SVG — not HTML — so the *same* widget works both as a DOM overlay on a
flat screen and inside a 3D scene on a `b3dSvgPlane` (where the panel is
serialized to a texture; HTML-in-`<foreignObject>` doesn't rasterize reliably).

## Layout protocol

You build a `panel3d` container and hand it widgets. The container gives each
widget its content **width**; each widget lays itself out and returns the
**height** it needs. The container stacks them and, if the stack overflows,
becomes scrollable (wheel + drag). Every widget defaults to ~40px tall.

## Binding

A widget's `value` may be a plain value *or* a tosijs reactive proxy (e.g.
`sky.timeOfDay`). When it's a proxy the widget reads/writes it and re-renders on
external change, so a `slider3d` and a native bound `<input>` stay in sync.

The example below has a **Test** tab: those assertions run in the doc system's
in-browser harness (real DOM + real pointer events) — coverage `bun test` can't
reach, since tosijs needs a DOM.

```js
import { tosi, elements } from 'tosijs'
import {
  panel3d, label3d, slider3d, toggle3d, button3d, text3d, list3d, colorPicker3d,
} from 'tosijs-3d'

const { ui } = tosi({ ui: { time: 8, fog: true, tint: '#4a6a9c99' } })
const { div, label, input } = elements

const panel = panel3d(
  { width: 340, height: 320 },
  label3d({ text: 'Scene settings' }),
  slider3d({ label: 'time of day', value: ui.time, min: 0, max: 24, step: 0.5 }),
  toggle3d({ label: 'fog', value: ui.fog }),
  // A colour WITH alpha: tap the swatch for the picker and its swatches.
  colorPicker3d({ label: 'fog tint', value: ui.tint }),
  button3d({ label: 'Reset', handleClick: () => { ui.time.value = 8; ui.fog.value = true } }),
  text3d({ text: 'Drag the slider — the native control below is bound to the same value.' }),
  list3d({
    items: [{ label: 'Talk' }, { label: 'Trade' }, { label: 'Leave' }],
    // Reports on the PAGE, not to the console: a demo you must open devtools to
    // read is one most people never see the output of — and it spams the console
    // of every page that embeds it.
    handleSelect: (item) => (readout.textContent = `picked: ${item.label}`),
  })
)

const readout = div({ style: { padding: '0 16px 12px', font: '13px system-ui', opacity: '0.75' } }, 'Pick a list item.')
// The tint laid over stripes, so its alpha is something you can see.
const chip = div({ style: { width: '120px', height: '60px', borderRadius: '8px', background: 'repeating-linear-gradient(45deg, #222 0 8px, #ddd 8px 16px)' } },
  div({ style: { width: '100%', height: '100%', borderRadius: '8px' } }))
const showTint = () => { chip.firstChild.style.background = ui.tint.value }
ui.tint.observe(showTint)
showTint()

preview.append(
  div(
    { style: {
      display: 'flex',
      gap: 24,
      padding: 16,
      alignItems: 'flex-start'
    } },
    panel,
    div({ style: { display: 'flex', flexDirection: 'column', gap: 12 } },
      label('native, same binding ', input({ type: 'range', min: 0, max: 24, step: 0.5, bindValue: ui.time })),
      chip),
  ),
  readout
)
```
```test
import { tosi, updates } from 'tosijs'
import { panel3d, slider3d, toggle3d, button3d, iconBar3d, list3d } from 'tosijs-3d'
const { s } = tosi({ s: { v: 0, on: false } })

test('slider reflects an external bound change', async () => {
  s.v = 0
  const panel = panel3d({ width: 300, height: 100 }, slider3d({ value: s.v, min: 0, max: 100 }))
  preview.append(panel)
  const knob = panel.querySelector('[data-w3d="slider"] circle')
  const before = Number(knob.getAttribute('cx'))
  s.v = 100 // a tosijs leaf is a boxed proxy — write through .value
  await updates() // let tosijs flush its update queue before asserting
  expect(Number(knob.getAttribute('cx'))).toBeGreaterThan(before)
})

// Widgets are coordinate-routed (no DOM events), so drive panel.handlePointer
// with viewBox coords — the same entry point the overlay and the in-scene/VR
// host both call. A down+up at a point = a click there.
test('toggle flips its bound value when the switch is clicked', async () => {
  s.on = false
  const panel = panel3d({ width: 300, height: 100 }, toggle3d({ label: 'sound', value: s.on }))
  preview.append(panel)
  // The switch is right-aligned (only it is interactive — the label area is
  // scroll surface), so click near the right edge, not the row centre.
  panel.handlePointer('down', 255, 30)
  panel.handlePointer('up', 255, 30)
  await updates()
  expect(s.on.value).toBe(true)
})

test('button fires handleClick on release', () => {
  let clicked = false
  const panel = panel3d({ width: 300, height: 100 }, button3d({ label: 'Go', handleClick: () => { clicked = true } }))
  preview.append(panel)
  panel.handlePointer('down', 150, 30)
  panel.handlePointer('up', 150, 30)
  expect(clicked).toBe(true)
})

test('iconBar toggles the icon under the pointer on release', () => {
  const hits = []
  const panel = panel3d({ width: 300, height: 100 }, iconBar3d({
    items: [
      { icon: 'barChart2', handleClick: () => hits.push('perf') },
      { icon: 'bug', handleClick: () => hits.push('bug') },
    ],
  }))
  preview.append(panel)
  // Buttons are 32px wide from the left padding, 6px gap → 2nd button ~x=50.
  // padding(12) + button0 spans ~12..44, button1 ~50..82. Click the 2nd.
  panel.handlePointer('down', 62, 30)
  panel.handlePointer('up', 62, 30)
  expect(hits).toEqual(['bug'])
})

test('list selects the clicked row', () => {
  const picks = []
  const panel = panel3d({ width: 300, height: 200 }, list3d({
    items: [{ label: 'A' }, { label: 'B' }],
    handleSelect: (item) => picks.push(item.label),
  }))
  preview.append(panel)
  // The second 40px row: viewBox y = padding(12) + ~58 lands in row B.
  panel.handlePointer('down', 150, 70)
  panel.handlePointer('up', 150, 70)
  expect(picks).toEqual(['B'])
})

test('panel clips when its content overflows', () => {
  const rows = Array.from({ length: 12 }, (_, i) => button3d({ label: 'row ' + i }))
  const panel = panel3d({ width: 300, height: 120 }, ...rows)
  preview.append(panel)
  expect(panel.querySelector('g[clip-path]')).toBeTruthy()
})
```

## In a 3D scene

The same panel rendered onto a `b3dSvgPlane`, interactive **the way VR needs
it**. Because the panel exposes `handlePointer`, `b3dSvgPlane` routes each pick's
texture UV → the panel's viewBox coords and lets the panel hit-test and capture
in its own SVG coordinate space — no DOM events, no `elementFromPoint`, no
`clientX`. That same path is fed by mouse, touch, **and XR controllers** (through
the scene's pointer observable), so the identical panel works as a DOM overlay,
on a flat canvas, and in immersive VR. Press **Enter VR** on a headset to drive
this exact panel with controllers.

```js
import { b3d, b3dLight, panelScene, panel3d, label3d, slider3d, toggle3d, list3d } from 'tosijs-3d'
import { tosi } from 'tosijs'

// distinct namespace — tosi() is a singleton keyed by path, so reusing `ui`
// here would collide with the first example's `ui`.
const { hud } = tosi({ hud: { hue: 200, glow: true } })

const HUES = { Warm: 30, Cool: 210, Lime: 90, Magenta: 320, Gold: 50 }
const panel = panel3d(
  // sized so the list overflows — scrolling is always part of the demo.
  { width: 280, height: 260 },
  label3d({ text: 'In-scene panel' }),
  slider3d({ label: 'hue', value: hud.hue, min: 0, max: 360, step: 1 }),
  toggle3d({ label: 'glow', value: hud.glow }),
  // logs which row was picked.
  list3d({ items: Object.keys(HUES).map((label) => ({ label })), handleSelect: (i) => console.log('list picked:', i.label) })
)
// Show it as a DOM overlay too (top-right) — the SAME panel object, so you can
// compare the in-DOM and on-plane surfaces side by side. It's also the texture
// source for the plane (b3dSvgPlane clones it each frame).
panel.style.position = 'absolute'
panel.style.top = '8px'
panel.style.right = '8px'
panel.style.zIndex = '1'
preview.append(panel)

// panelScene packages the plane, camera and pick routing (uv → viewBox
// coords → the panel's handlePointer, mouse AND XR controllers) with
// camera-yield and capture semantics.
const { plane, sceneCreated } = panelScene({ svg: panel, target: panel, resolution: 512, camera: { beta: Math.PI / 2.4, radius: 4 } })

const sceneEl = b3d({ sceneCreated }, b3dLight(), plane)
preview.append(sceneEl)
// No manual Enter-VR button needed — b3d offers one automatically whenever an
// immersive-vr session is supported (suppress it with the `no-xr` attribute).
```

## Dual-presence scene panel

The same widgets, authored once, drive a panel that has a presence **both in the
flat view and in a headset**. Pass `b3d` a `scenePanel` hook returning the
widgets: the **owl** on the system bar (top left, flat; up ahead in a headset)
opens them as a panel drawn in the scene, under the bar. In VR the bar also
carries **Exit VR** (you can't click a DOM button inside a headset).
`flat-ui="dom"` puts the flat panel back in an HTML overlay. Both presentations
bind to the same reactive values, so they stay in sync.

Click the owl to raise/lower and spin the cube — then try it in VR.

```js
import { b3d, b3dLight, b3dBox, label3d, toggle3d, slider3d } from 'tosijs-3d'
import { tosi } from 'tosijs'

const { cfg } = tosi({ cfg: { spin: true, height: 1 } })

const cube = b3dBox({ meshName: 'cube', size: 1, y: 1, color: '#39c5ff' })

// b3dBox is an AbstractMesh: it drives mesh.rotationQuaternion from its rx/ry/rz,
// and a mesh WITH a rotationQuaternion ignores its euler `rotation`. So nudging
// cube.mesh.rotation does nothing — set the quaternion directly. Tumble on two
// axes so a single-colour cube under flat lighting clearly reads as spinning.
let spin = 0
const sceneEl = b3d(
  {
    panelTitle: 'Scene settings',
    scenePanel: () => [
      toggle3d({ label: 'spin', value: cfg.spin }),
      slider3d({ label: 'height', value: cfg.height, min: 0, max: 3, step: 0.1 }),
    ],
    update(el, BABYLON) {
      if (!cube.mesh) return
      cube.mesh.position.y = cfg.height.value
      if (cfg.spin.value) {
        spin += 0.02
        cube.mesh.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(spin, spin * 0.6, 0)
      }
    },
  },
  b3dLight(),
  cube
)
preview.append(sceneEl)
```

## Widget reference

Every widget's options in one place. Note `slider3d`'s `step`: it quantises the
drag *and* the reported value, so a slider can be discrete without becoming a
`select3d`.

| widget | option | default | notes |
| --- | --- | --- | --- |
| `panel3d` | `width` | `360` | |
| | `height` | `'fit'` | a number to fix it; `'fit'` sizes to content |
| | `maxHeight` | — | cap for `'fit'`; past it the panel scrolls |
| | `padding` / `paddingTop` / `gap` | `12`/`padding`/`4` | gap is half the theme spacing |
| | `background` | theme `panelBg` | |
| `label3d` | **`collapsible`** | `false` | a SECTION header: tap to fold everything below it, up to the next collapsible label. A `<tosi-b3d>` `scenePanel` folds them itself; for a `panel3d` of your own, run the rows through `foldSections(rows, { key, mode, repaint })` and rebuild on `repaint` |
| | `open` | first section only | whether a collapsible section starts open |
| `row3d` | `weights` | equal | proportional shares of the post-gap width |
| | `align` | `'middle'` | `top` / `middle` / `bottom` |
| | `gap` | `8` | |
| `slider3d` | `min` / `max` | `0` / `1` | |
| | **`scale`** | `'linear'` | `'log'` / `'log2'` — equal travel per decade / octave |
| | **`snap`** | `0` | quantise the VALUE (e.g. `1` for whole grid sizes) |
| | **`zeroStop`** | `false` | an explicit `0` at the bottom of a log track; `min` becomes the log floor |
| | **`step`** | `0` | **quantises the drag and the value**; `0` is continuous |
| | `showValue` | `'peek'` | `peek` (on touch/drag) / `always` / `never` |
| | `format` | step-derived | `(v) => string` — units, precision |
| `toggle3d` | `label` / `value` | | |
| `select3d` | `options` | | opens a list (downward chevron); steps only with no host |
| | `stayOpen` | `false` | keeps the list open after a pick, until closed or pressed behind: for trying options one after another |
| `inputField` | **`type`** | `'text'` | `text`/`number`/`integer`/`email`/`url`/`tel` |
| | `placeholder` / `value` / `height` / `fontSize` | | |
| `list3d` | `items` / `handleSelect` | | items may carry `icon` and `disabled` |
| `button3d` | `label` / `handleClick` | | |
| | `menu` | — | makes it a MENU button — opens actions instead of firing `handleClick` |
| `menu3d` | `items` / `handleSelect` | | rows of `MenuAction`; usually via `openMenu3d` |
| `iconBar3d` | `items` | | `{icon, handleClick}` |
| `foldSections` | `key` / `mode` / `repaint` | `'fold'` | the rows to show for collapsible sections: folded one open at a time (`'fold'`, an accordion), folded with several open (`'folds'`), or as `tabs3d` (`'tabs'`); rebuild your panel on `repaint` |
| `tabs3d` | `tabs` / `active` / `handleSelect` | `0` | file tabs; a tab is a caption or `{ icon, title }`; at most half the strip each, overlapping around the active one when they don't fit |
| `spinner3d` | `label` / `size` | | INDETERMINATE busy — call `dispose()` when done |
| `progress3d` | `label` / `value` / `showValue` | | determinate `0..1`; `setValue(f)` |

## Menus are for ACTIONS; a select is for a VALUE

`select3d` keeps and displays what you chose. A menu item **happens** and leaves
nothing behind — so using a picker for a one-shot reads wrong, showing a
lingering "value" for something that was an event.

`openMenu3d(host, anchor, items)` is the whole API. Any widget handed a
`WidgetHost` via `setHost` can open one, so an icon in a tool palette can open
a menu as readily as `select3d` opens its list.

```javascript
// Declared ONCE — `disabled` is a predicate, so this constant never goes stale.
const FILE_MENU = [
  { label: 'Recent scene', icon: 'star', handleSelect: loadRecent },
  { label: 'From file…', icon: 'uploadCloud', handleSelect: pickFile },
  { label: 'Revert', icon: 'rotateCcw', disabled: () => !doc.dirty, handleSelect: revert },
]
openMenu3d(host, cellRect, FILE_MENU)
```

**`disabled` takes a predicate, and usually should.** A boolean captures the
state at construction, so the only way to keep it honest is to rebuild the
array — which means the menu cannot be a constant, and every consumer reinvents
rebuild-on-change plumbing. A predicate is asked each time it matters, so one
array stays correct forever. (A plain boolean still works, for something
genuinely constant.) Same for `IconGridItem.disabled`, so a tool palette can be
a constant for the same reason.

Three rules it enforces, each of which is a bug if you get it wrong:

- **A disabled item is present, greyed, and inert.** Not hidden — a menu whose
  items come and go reflows under you, so the same command is at a different
  place depending on state and muscle memory never forms. It is also checked at
  activation rather than at hover, so an item that becomes unavailable
  mid-gesture cannot fire — which only means anything *because* `disabled` can
  be a predicate that changed while the menu was open.
- **The menu closes before any handler runs.** An action that opens something of
  its own — a confirm, a file picker, another menu — would otherwise be torn
  down a moment later by its parent closing on top of it.
- **A menu cell in an `iconGrid3d` never joins the selection**, whatever the
  grid's mode. Otherwise opening "Load ▾" in a radio palette silently deselects
  your active tool, and dismissing without choosing leaves the palette lying
  about which tool is live.

**On a panel** (not options — methods on the returned element):
`measure()` → `{content, viewport, overflow, fits}`, `openPopup(config, …widgets)`,
`handlePointer(kind, x, y)`, `scrollBy(dy)`, `scrollable`, `scrollTop` (read and
set: carry it across a rebuild) and `reveal(widget, band?)` (scroll a row into
view, in a headset too).

**On a field**: `type`, `keyboardMode`, `isValid()`, `commit()`, plus the edit
protocol (`insert`, `action`, `setValue`, `moveCaret`). `fieldGroup` manages
several of them — exclusivity, commit-on-leave and keyboard layout.

## Tabs

`tabs3d` is a strip of FILE TABS: pick one of several pages. Each tab is at
most half the strip wide, its caption ellipsized. When they fit, they sit
side by side; when they don't, they OVERLAP around the active tab, those to
its left sharing the space before it and those to its right the space after,
stacked toward it, so every tab stays reachable. Pick one on the left and one
on the right and watch the strip re-spread. A tab can be an icon instead
(`{ icon, title }`); icons are narrow, so an icon strip rarely overlaps.

In a `<tosi-b3d>` scene panel you do not build the strip yourself:
`panelSections="tabs"` turns `label3d({ collapsible: true })` sections into
tabs, and a section's `icon` becomes its tab.

```js
import { elements } from 'tosijs'
import { panel3d, tabs3d, label3d } from 'tosijs-3d'

const { div } = elements
const readout = div({ style: { padding: '0 16px 12px', font: '13px system-ui', opacity: '0.75' } }, 'Pick a tab.')
const say = (strip) => (i) => (readout.textContent = `${strip}: ${i}`)

const SECTIONS = ['World', 'Terrain', 'Climate', 'Weather', 'Atmosphere', 'Stars', 'Moons', 'Vegetation', 'Camera']
const FEW = ['General', 'Graphics', 'Audio']
const ICONS = [
  { icon: 'earth', title: 'World' },
  { icon: 'terrain', title: 'Terrain' },
  { icon: 'thermometer', title: 'Climate' },
  { icon: 'cloud', title: 'Weather' },
  { icon: 'moon', title: 'Moons' },
  { icon: 'tree', title: 'Vegetation' },
]

preview.append(
  div(
    { style: { display: 'flex', flexDirection: 'column', gap: 16, padding: 16 } },
    panel3d({ width: 320 }, label3d({ text: 'Nine captions: they overlap', muted: true }),
      tabs3d({ tabs: SECTIONS, active: 3, handleSelect: (i) => say('captions')(SECTIONS[i]) })),
    panel3d({ width: 320 }, label3d({ text: 'Three captions: side by side', muted: true }),
      tabs3d({ tabs: FEW, handleSelect: (i) => say('few')(FEW[i]) })),
    panel3d({ width: 320 }, label3d({ text: 'Icons', muted: true }),
      tabs3d({ tabs: ICONS, active: 3, handleSelect: (i) => say('icons')(ICONS[i].title) })),
    readout
  )
)
```

## Saying that something is HAPPENING

`spinner3d` for "working, duration unknown"; `progress3d(fraction)` when the
total is genuinely known. Both are `Widget3d`s, so the thing that is loading is
one ROW of a panel rather than a mode the whole panel enters — which is usually
the truth.

**The spinner animates as geometry, not CSS**, and that is the reason it lives
here rather than in each consumer. Flat, a keyframe animation works. Rasterised
to an in-scene texture it does not run at all: `SvgTexture` serialises the SVG
into its own document, where no stylesheet and no animation clock follow it. A
consumer would ship a spinner that looks right on a desktop and silently freezes
in a headset.

One shared ticker drives every spinner, so N spinners are not N timers, and the
timer stops when the last one goes. **Call `dispose()` when the work ends** —
a forgotten spinner keeps that ticker alive and keeps painting something that is
no longer true.

If you find yourself faking a fraction, you wanted `spinner3d`: a determinate
bar that is not determinate is a lie with a progress percentage on it.

## Callbacks are `handleX`, not `onX`

`handleChange`, `handleClick`, `handleSelect`. An `onX` option is ignored, and
passing one warns once.

Not a style preference. If a factory becomes a tosijs COMPONENT, the element
creator binds an `on*` prop as a DOM event **listener** and the class field is
silently never called: no error, no warning, a callback that never fires.
`handleX` cannot be mistaken for an event name, so the name removes the trap
rather than documenting it.

## Log sliders — for anything spanning orders of magnitude

`scale: 'log'` gives every DECADE equal travel; `scale: 'log2'` every octave.
Reach for one whenever the useful values are multiplicative rather than
additive — a light's intensity, a **noise scale**, a grid or texture size, a
frequency, a zoom factor.

The tell is a linear slider where everything you want lives in the first few
percent of the track: an intensity slider from 0 to 1000 leaves almost no
travel for 0 to 1.

```javascript
slider3d({ label: 'intensity', value: 2, min: 0.01, max: 1000, scale: 'log' })
// A rate that can also be OFF — 0 is the default and must stay reachable.
slider3d({ label: 'sky speed', value: 0, min: 0.1, max: 3600, scale: 'log', zeroStop: true })
slider3d({ label: 'noise scale', value: 0.05, min: 0.001, max: 10, scale: 'log' })
slider3d({ label: 'grid', value: 16, min: 1, max: 256, scale: 'log2', step: 1 })
```

**`step` and `snap` answer different questions**, and both are useful:

- `step` is how far one notch moves you, in the SCALE's units — octaves on
  `log2`, so `step: 1` walks 1, 2, 4, 8.
- `snap` is which values are legal at all, in plain units — `snap: 1` keeps a
  grid size a whole number however it was reached.

`log` and `log2` place the handle identically (the base cancels in the mapping);
they differ only in what a step means. A range including zero falls back to
linear rather than producing NaN, because a slider that silently stops working
is worse than one that is merely the wrong shape.

**`zeroStop` when the quantity has an explicit OFF.** A log scale cannot
represent zero, but plenty of decade-spanning values can be switched off — a
sky's `realtimeScale` (0 is a still sky, and it is the default), fog density,
wind speed, any rate. Without it the default becomes unreachable the moment you
touch the control. With it, the bottom slice of the track IS zero and `min`
means the log floor; the handle catches at off rather than approaching it
asymptotically.

## Theming

Widget colours, font, and weights are driven by `--w3d-*` CSS variables with
sensible defaults. Set them on `:root` **before the bundle loads**, or call
`setW3dTheme` and rebuild; they are resolved once, for the reason given on
[w3d-theme](/w3d-theme/), which also has a live theme editor. Per-widget
options (`label3d`'s `color` / `bold`) override the variables.

| Variable | Default | Controls |
|----------|---------|----------|
| `--w3d-text` | `#f0f0f0` | Primary text/label colour |
| `--w3d-muted` | `#9aa0a6` | Muted/secondary text |
| `--w3d-heading-weight` | `700` | Weight of `bold` labels/headings |
| `--w3d-text-weight` | `400` | Weight of normal text |
| `--w3d-font-size` | `16` | Base font size (px) |
| `--w3d-font-family` | `system-ui, sans-serif` | Font family |
| `--w3d-accent` | `#39c5ff` | Slider fill / active accent |
| `--w3d-track` | `#3a3f4a` | Slider/toggle track |
| `--w3d-panel-bg` | `rgba(20,22,28,0.94)` | Panel background |
| `--w3d-button-bg` / `--w3d-button-hover` / `--w3d-button-active` | greys | Button states |
| `--w3d-row-bg` / `--w3d-row-hover` | subtle whites | Row background / hover |

## Layout helpers

The maths behind the layout protocol is pure (no DOM, no Babylon) and exported,
for a consumer laying out SVG of its own:

- `stackLayout(heights, gap)` → `{ offsets, total }`: a column, top to bottom.
- `panelFit(content, viewport)` → whether a stack overflows, so must scroll.
- `measureTextWrap(text, width, font)` breaks a paragraph into lines by real
  glyph measurement (canvas `measureText`), not a guessed character count, so a
  panel wraps where the text actually overflows. `cssFont(spec)` builds the
  font string; `measureTextWidth` measures one line.
- `valueToFraction` / `fractionToValue` map a slider value to its track
  position and back, for the `linear`, `log` and `log2` scales.
*/
/*{ "parent": "ui", "order": 10 }*/

import { svgElements, StyleSheet } from 'tosijs'
import { placePopup, type PopupSide } from './flow-layout.js'
import {
  alignOffset,
  panelFit,
  panelHeight,
  rowColumns,
  type RowColumn,
  type PanelFit,
  stackLayout,
  clampScroll,
  measureTextWrap,
  valueToFraction,
  fractionToValue,
  DEFAULT_SLIDER_PRECISION,
  type SliderScale,
  type FontSpec,
  ellipsize,
  measureTextWidth,
  tabLayout,
  tabAt,
  tabOrder,
} from './widgets3d-layout.js'
import { handlerOf, resetHandlerWarnings } from './handler-of.js'
import { w3dTheme } from './w3d-theme.js'
import { iconGlyph } from './svg-icons.js'
import { chromeLayout } from './popup-chrome.js'
import { panelRim } from './panel-rim.js'

const { svg, g, rect, text, circle, clipPath } = svgElements

// --- Theme (configurable later) --------------------------------------------

/*
ROW AND PAD_X ARE DERIVED, not fixed.

They were 40 and 12, so `padding` and `lineHeight` could not affect a button or
a field — Tonio: "lineHeight doesn't seem to have any effect. We also need a
padding value that makes buttons and fields more spacious." Both were true: the
one place `lineHeight` reached was stacked text, and nothing set a control's
inner padding at all.

A row is now one line of text plus padding above and below, which is what a
control's height actually is — so raising `padding` makes controls roomier in
both axes, and `lineHeight` reaches everything rather than only text blocks.
Defaults (16 × 1.35 + 12 × 2 ≈ 46) sit close to the old 40.
*/
const PAD_Y = 8

// Widget styling comes from the `--w3d-*` CSS variables, resolved ONCE at load
// in w3d-theme (the full rationale — texture rasterization can't see the page's
// custom properties — lives there). Local names keep the paint code brief.

// Compact line height for stacked text (text3d / textBlock3d) — a fraction of a full
// interactive ROW, which is what buys the vertical space back on text-heavy panels.
// One FontSpec, used BOTH to measure and to stamp the matching font-* attributes on
// the <text>, so measurement and rendering can't drift out of sync.

/*
LIVE THEME READS — these were `const X = w3dTheme.x` at module scope.

That captured the palette the instant the module was IMPORTED, so
`setW3dTheme` could never reach them: the constants already held the old
values, and a theme editor changed nothing. Only `roundedRadius` appeared to
work, because it happened to be read inline at construction.

Getters make every read happen when a widget is BUILT, which is the contract
the theme documents. The names stay so the paint code reads the same.
*/
const TH = {
  get GAP() {
    return w3dTheme.spacing
  },
  get FONT() {
    return w3dTheme.fontSize
  },
  get FONT_FAMILY() {
    return w3dTheme.fontFamily
  },
  get TEXT() {
    return w3dTheme.text
  },
  get MUTED() {
    return w3dTheme.muted
  },
  get HEADING_WEIGHT() {
    return w3dTheme.headingWeight
  },
  get TEXT_WEIGHT() {
    return w3dTheme.textWeight
  },
  get PANEL_BG() {
    return w3dTheme.panelBg
  },
  get BTN_BG() {
    return w3dTheme.buttonBg
  },
  get BTN_HOVER() {
    return w3dTheme.buttonHover
  },
  get BTN_ACTIVE() {
    return w3dTheme.buttonActive
  },
  get TRACK() {
    return w3dTheme.track
  },
  get ACCENT() {
    return w3dTheme.accent
  },
  get ROW_BG() {
    return w3dTheme.rowBg
  },
  get ROW_HOVER() {
    return w3dTheme.rowHover
  },
  get PAD_X() {
    return w3dTheme.padding
  },
  /** One line of text plus padding above and below — what a control's height is. */
  /*
  Vertical padding is three quarters of the horizontal. Rows padded fully
  above and below, with a full gap between them, read as spaced out. Tonio:
  "we can probably tighten the space between items in the stack. We don't
  want to crowd them but they're quite spaced out right now."
  */
  get ROW() {
    return Math.round(
      w3dTheme.fontSize * w3dTheme.lineHeight + w3dTheme.padding * 1.5
    )
  },
  /** Compact line height for stacked text — a fraction of a full interactive ROW. */
  get LINE_H() {
    return Math.round(w3dTheme.fontSize * w3dTheme.lineHeight)
  },
  /** One FontSpec used BOTH to measure and to stamp font-* attributes, so the
   * two cannot drift. */
  get TEXT_FONT(): FontSpec {
    return {
      size: w3dTheme.fontSize,
      family: w3dTheme.fontFamily,
      weight: w3dTheme.textWeight,
    }
  },
  get BOLD_FONT(): FontSpec {
    return { ...TH.TEXT_FONT, weight: w3dTheme.headingWeight }
  },
}

let clipSeq = 0

/**
 * A pointer phase, routed by the panel in the widget's local SVG coords.
 * `hover`/`leave` give feedback without a press (e.g. a VR controller ray
 * crossing the panel); `down`/`move`/`up` are a press/drag/release.
 */
export type PointerKind = 'down' | 'move' | 'up' | 'hover' | 'leave'

/** A laid-out widget: its SVG group, sizing, and coordinate-based interaction. */
/**
 * A value, or a function that produces it when asked.
 *
 * The point is that a menu, a palette or a toolbar can be a **constant** — built
 * once at module scope and reused — instead of being rebuilt every time the
 * state it depends on moves. Tonio: _"disabled should be a callback function not
 * a static value. This allows a menu to be a reusable object and not have to be
 * built per use."_
 *
 * With a static boolean, `{ label: 'Revert', disabled: !dirty }` captures
 * `dirty` at construction, so the only way to keep it honest is to rebuild the
 * array — which means the menu cannot be a constant, and every consumer invents
 * their own rebuild-on-change plumbing.
 */
export type Dynamic<T> = T | (() => T)

// `handlerOf` LIVES in its own module now, so `box`/`surface` can adopt it
// without pulling this one into their import graph. Re-exported because it was
// public here first, and a moved export is a breaking change for no gain.
export { handlerOf, resetHandlerWarnings }

/** Read a `Dynamic`, falling back when it was never given. */
export function resolveDynamic<T>(v: Dynamic<T> | undefined, fallback: T): T {
  return typeof v === 'function' ? (v as () => T)() : v ?? fallback
}

export interface Widget3d {
  el: SVGElement
  /** Lay out internals to `width`px; return the height consumed (px). */
  layout(width: number): number
  /**
   * Handle a pointer at widget-local SVG coords (0,0 = the widget's top-left).
   * Coordinate-based, NOT DOM events — so it works identically as a flat-screen
   * overlay and in-scene/VR (where input arrives via the scene's pointer
   * observable, not the canvas). Omit for non-interactive widgets.
   */
  handle?(kind: PointerKind, x: number, y: number): void
  /**
   * Whether widget-local (x,y) falls on the *interactive control* (vs dead row
   * space). The panel only captures/highlights inside it; everywhere else the
   * row is treated as scroll-drag surface. Omit to treat the whole row as the
   * control (button, list row). Lets you grab "between" a switch/slider to
   * scroll — important in VR where pointing precisely is hard.
   */
  hitTest?(x: number, y: number): boolean
  /**
   * The host container reflects hover/press/focus into the widget so it can
   * restyle — an input field brightens its caret while it holds the panel's
   * focus and dims it when focus moves on (with two fields on a panel, the
   * caret IS the focus indicator).
   */
  setState?(state: {
    hovered: boolean
    pressed: boolean
    focused: boolean
  }): void
  /**
   * Inner focus traversal, for a widget that is a whole surface of controls
   * (the keyboard's keys) rather than one control. Same escape contract as
   * `BoxChild.focusMove` / `table.focusMove`: return `true` if the D-pad move
   * landed inside, `false` if focus escaped in that direction so the host moves
   * on. Also called on entry with the direction of travel, to seed focus at the
   * matching edge. A widget that implements this draws its own focus indicator.
   */
  focusMove?(dx: number, dy: number): boolean
  /** Activate the inner-focused item (Enter / A). Pairs with `focusMove`. */
  focusActivate?(): void
  /** Drop inner focus — the host's focus left this widget. */
  focusClear?(): void
  /**
   * The host made something else the receiver.
   *
   * Distinct from `focusClear`, which means D-pad focus moved on — that does NOT
   * stop text arriving at a field, because tapping the on-screen keyboard's keys
   * moves box focus to the keyboard while the text keeps landing where it was.
   * This one is the real end of an interaction, and is what puts a summoned
   * keyboard away.
   */
  setActive?(active: boolean): void
  /**
   * Called once by the containing panel, handing the widget the services only
   * the panel can provide.
   *
   * A widget cannot reach its own panel otherwise — it is constructed BEFORE the
   * panel that will hold it (`panel3d({}, select3d(...))`), so it cannot be
   * passed one, and it is not a DOM child in any useful sense. This is the seam
   * that lets a control open a popup without the consumer wiring it up.
   */
  setHost?(host: WidgetHost): void
  /**
   * Release anything that outlives the element — a timer, an observer, a
   * registration in a shared pool.
   *
   * Called by whoever BUILT the widget list, when that list is replaced. Most
   * widgets need nothing; a widget that animates does, because its element
   * going out of the tree does not stop a timer holding a reference to it.
   *
   * It has to live on the interface rather than on the one widget that needs
   * it: `B3d` rebuilds its panel rows on every repaint and cannot know which
   * of a consumer's widgets registered something. Without a name it can call,
   * it leaks whatever it was handed.
   */
  dispose?(): void
}

/**
 * Mounts an unbounded layer over a panel. Installed by whatever owns the panel's
 * presentation — `panelScene` in a scene, the app when flat.
 */
export type LayerHost = (
  /**
   * The popup's SVG, already built — each presentation MOUNTS it rather than
   * building its own.
   *
   * Handing every host the same widget INSTANCES did not work and could not:
   * `appendChild` moves a node, so whichever host built last stole the widgets
   * and the others got an empty sheet. (Observed as a keyboard plane that
   * mounted with no content on it.)
   *
   * One sheet, mounted more than once, is also how the panel itself already
   * works — `SvgTexture` CLONES the live element per frame, so a sheet can be in
   * the DOM and on a plane at once and stay a single UI rather than two that
   * drift.
   */
  sheet: SVGSVGElement,
  config: {
    anchor: { x: number; y: number; width: number; height: number }
    side?: PopupSide
    width?: number
    maxHeight?: number
    /**
     * Call this if the host closes the popup ITSELF — its own × glyph, say.
     *
     * A layer popup is ONE popup mounted by every presentation, so a close that
     * starts in one of them has to reach the others. Without it, pressing × on
     * the in-scene face closed that plane alone and left the flat one up, with
     * the opener still believing it had a popup open.
     */
    handleClosed?: () => void
    /**
     * Headroom reserved in the sheet for move/close glyphs, in viewBox units.
     *
     * `0` means the caller asked for NO chrome, and a host must then draw none:
     * the band and the glyphs are one decision, and a host that draws without a
     * reservation puts them on the content ("the move affordance overlaps the
     * 'q'"), while a reservation nobody draws into is the empty strip above
     * every flat menu ("a lot of wasted space up top"). Both complaints are on
     * record; this is the single number that settles them together.
     */
    chromeBand?: number
  }
) => { close: () => void }

/**
 * Build the SVG a layer mounts. Separate so every presentation shares one.
 *
 * `paddingTop` reserves HEADROOM for the popup chrome. `popup-surface` draws its
 * move and close glyphs into the top of whatever SVG it is given, so without a
 * band kept clear they land on the content — Tonio: "the move affordance
 * overlaps the 'q' and the close overlaps another key". The chrome is drawn by
 * the mounting layer and the sheet is built here, so the sheet is the only place
 * that can leave room for it.
 */
const POPUP_CHROME_BAND = 30
/*
A popup is capped and SCROLLS past the cap. Uncapped, Perf Stats with a sweep
listed was taller than the screen flat and "enormous" in a headset, with no way
to reach its lower rows in either.
*/
const POPUP_MAX_HEIGHT = 480
/**
 * @param chromeBand headroom for the mounting layer's own move/close glyphs.
 *   `0` when no mounting host draws any — a DOM layer does not, and reserving
 *   the band regardless put an empty 30px strip above every flat menu. Tonio:
 *   "The popup is good but it has a lot of wasted space up top."
 *
 *   It is a property of the SHEET, and the sheet is shared by every
 *   presentation, so a panel shown both flat and on a plane still reserves it —
 *   the alternative is two sheets, which is the divergence this whole
 *   arrangement exists to avoid. Paying 30px flat so the in-scene one has room
 *   for its handles is the right side of that trade.
 */
function panelPopupSheet(
  width: number,
  items: Widget3d[],
  chromeBand: number = POPUP_CHROME_BAND,
  maxHeight: number = POPUP_MAX_HEIGHT
): SVGSVGElement {
  return panel3d(
    { width, height: 'fit', maxHeight, paddingTop: chromeBand },
    ...items
  )
}

/**
 * A collapsible section header: bold caption, a chevron (open / closed), and
 * a tap that asks the host to fold or unfold. `section.toggle` and
 * `section.isOpen` are wired by the host before layout.
 */
function sectionLabel(
  config: { text: string; open?: boolean; color?: string; icon?: string },
  fill: string
): Widget3d & {
  section: {
    title: string
    icon?: string
    open?: boolean
    isOpen: boolean
    toggle?: () => void
  }
} {
  const section = {
    title: config.text,
    icon: config.icon,
    open: config.open,
    isOpen: config.open ?? false,
    toggle: undefined as undefined | (() => void),
  }
  const t = baseText(config.text, fill, true)
  const chevron = baseText('', TH.MUTED, true)
  // COMPACT (Tonio: "the collapsed sections could be a little more compact"):
  // a caption line, not a control row, so a panel of folded sections reads as
  // a tight table of contents. Still a comfortable target in a headset.
  const h = Math.max(TH.LINE_H + 8, 28)
  const bg = rect({
    x: 0,
    y: 1,
    rx: 6,
    ry: 6,
    height: h - 2,
    fill: 'transparent',
  })
  t.setAttribute('x', String(TH.PAD_X + 16))
  t.setAttribute('y', String(h / 2))
  chevron.setAttribute('x', String(TH.PAD_X))
  chevron.setAttribute('y', String(h / 2))
  return {
    el: g({ 'data-w3d': 'section' }, bg, chevron, t),
    section,
    layout(width) {
      bg.setAttribute('width', String(width))
      chevron.textContent = section.isOpen ? '▾' : '▸'
      t.textContent = ellipsize(
        config.text,
        width - TH.PAD_X * 2 - 16,
        TH.BOLD_FONT
      )
      return h
    },
    handle(kind) {
      bg.setAttribute('fill', kind === 'leave' ? 'transparent' : TH.BTN_HOVER)
      if (kind === 'up') section.toggle?.()
    },
  }
}

/**
 * **A strip of FILE TABS** (board #2466): pick one of several named pages.
 * Each tab is at most half the strip wide with its label ellipsized; when
 * they do not fit they overlap, each later tab on top of the one before, and
 * the ACTIVE tab sits wholly in front, joined to what is below it. A tap goes
 * to whatever tab is visibly on top there. Geometry is `tabLayout` / `tabAt`
 * (pure, tested).
 */
/** A tab: a caption, or an ICON (with an optional title for its name). */
export type Tab3d = string | { icon: string; title?: string }

export function tabs3d(config: {
  tabs: Tab3d[]
  /** Index of the active tab. */
  active?: number
  handleSelect?: (index: number) => void
}): Widget3d {
  const H = 30
  const R = 7
  const TAB_ICON = 18
  let active = Math.max(0, Math.min(config.tabs.length - 1, config.active ?? 0))
  let layoutW = 0
  const root = g({ 'data-w3d': 'tabs' })
  const tabPath = (x: number, w: number) =>
    `M${x},${H} L${x},${R} Q${x},0 ${x + R},0 L${x + w - R},0 Q${x + w},0 ${
      x + w
    },${R} L${x + w},${H} Z`
  const draw = () => {
    root.replaceChildren()
    const n = config.tabs.length
    // Side by side when the labels allow, at most half the strip each.
    // ICON tabs are narrow, so a strip of them rarely overlaps (and so does
    // not move when the active tab changes).
    const natural = Math.max(
      ...config.tabs.map((t) =>
        typeof t !== 'string'
          ? TAB_ICON + TH.PAD_X * 2
          : measureTextWidth(t, TH.BOLD_FONT) + TH.PAD_X * 2
      )
    )
    const tabW = (t: Tab3d) =>
      typeof t !== 'string'
        ? TAB_ICON + TH.PAD_X * 2
        : measureTextWidth(t, TH.BOLD_FONT) + TH.PAD_X * 2
    const L = tabLayout(
      n,
      layoutW,
      0.5,
      natural,
      active,
      tabW(config.tabs[active])
    )
    ;(root as any).__layout = L
    // The line the active tab joins: the strip's floor.
    root.appendChild(
      rect({ x: 0, y: H - 1, width: layoutW, height: 1, fill: TH.BTN_BG })
    )
    for (const i of tabOrder(n, active)) {
      const on = i === active
      const x = L.x[i]
      // OPAQUE: the theme's fills are translucent, and a tab must hide the
      // label of the one it covers. The panel colour underneath, the tint on
      // top.
      // The active tab may be narrower than the rest (its own caption).
      const tw = L.ws[i]
      const under = svgElements.path({ d: tabPath(x, tw), fill: TH.PANEL_BG })
      const tab = svgElements.path({
        d: tabPath(x, tw),
        fill: on ? TH.BTN_BG : TH.ROW_BG,
        stroke: TH.PANEL_BG,
        'stroke-width': 1.5,
      })
      const item = config.tabs[i]
      if (typeof item !== 'string') {
        const [v0, v1] = L.visible[i]
        const cx = on ? x + tw / 2 : (v0 + v1) / 2
        root.appendChild(
          g(
            {},
            under,
            tab,
            iconGlyph(item.icon, {
              color: on ? TH.TEXT : TH.MUTED,
              size: TAB_ICON,
              x: cx - TAB_ICON / 2,
              y: (H - TAB_ICON) / 2,
            })
          )
        )
        continue
      }
      const label = baseText('', on ? TH.TEXT : TH.MUTED, on)
      /*
      THE LABEL SITS IN THE VISIBLE SLICE. Left of the active tab the slice
      is the tab's left end (later tabs cover the rest); right of it, its
      right end. The active tab gets its whole width. A slice too narrow for
      an ellipsis shows the name's first letter.
      */
      const [v0, v1] = L.visible[i]
      const room = (on ? tw : v1 - v0) - 12
      label.setAttribute('x', String((on ? x : v0) + (on ? TH.PAD_X : 6)))
      label.setAttribute('y', String(H / 2 + 1))
      const font = on ? TH.BOLD_FONT : TH.TEXT_FONT
      let shown = ellipsize(item, Math.max(1, room), font)
      if (shown === '…' || shown === '') shown = item.slice(0, 1)
      label.textContent = shown
      root.appendChild(g({}, under, tab, label))
    }
  }
  return {
    el: root,
    layout(width) {
      layoutW = width
      draw()
      return H + 4
    },
    handle(kind, x) {
      if (kind !== 'up') return
      const i = tabAt(x, (root as any).__layout, active)
      if (i < 0 || i === active) return
      active = i
      draw()
      config.handleSelect?.(i)
    },
  }
}

/**
 * **Fold or tab a panel's sections** (board #2805, tosijs-3d#99). A
 * `label3d({ collapsible: true })` heads a section: every row after it, up to
 * the next one, belongs to it. This returns the rows to SHOW:
 *
 * - `mode: 'fold'` (the default): every header, plus the rows of the open
 *   section. One is open at a time, as an accordion: opening one closes the
 *   others, and all may be closed. Of sections declared or saved open, the first wins.
 * - `mode: 'folds'`: the same, but several may be open at once.
 * - `mode: 'tabs'`: rows before the first section, a `tabs3d` strip of the
 *   sections (a section's `icon` becomes its tab), and the active section's rows.
 *
 * A panel is laid out once when built, so folding means REBUILDING: a header's
 * tap or a tab's pick calls `repaint`, and you build the panel again from the
 * same row list. Pass `key` to remember the open set and the active tab across
 * visits (localStorage; a private window just starts from the defaults).
 * `<tosi-b3d>`'s scene panel is built on this.
 *
 * ```javascript
 * const build = () => panel3d({ width: 320 }, ...foldSections(rows(), { key: 'sky', repaint: rebuild }))
 * ```
 */
export function foldSections(
  rows: Widget3d[],
  opts: { key?: string; mode?: 'fold' | 'folds' | 'tabs'; repaint: () => void }
): Widget3d[] {
  const state = sectionState(opts.key)
  const save = () => {
    if (opts.key == null) return
    try {
      localStorage.setItem(
        `${opts.key}`,
        JSON.stringify(Object.fromEntries(state.open))
      )
      if (state.tab != null) localStorage.setItem(`${opts.key}:tab`, state.tab)
    } catch {
      /* no storage: it still works for this visit */
    }
  }
  type Sec = {
    title: string
    icon?: string
    open?: boolean
    isOpen: boolean
    toggle?: () => void
  }
  if (opts.mode === 'tabs') {
    const head: Widget3d[] = []
    const sections: { title: string; icon?: string; rows: Widget3d[] }[] = []
    for (const row of rows) {
      const sec = (row as any).section as Sec | undefined
      if (sec != null)
        sections.push({ title: sec.title, icon: sec.icon, rows: [] })
      else if (sections.length === 0) head.push(row)
      else sections[sections.length - 1].rows.push(row)
    }
    if (sections.length === 0) return rows
    let active = sections.findIndex((x) => x.title === state.tab)
    if (active < 0) active = 0
    const strip = tabs3d({
      tabs: sections.map((x) =>
        x.icon ? { icon: x.icon, title: x.title } : x.title
      ),
      active,
      handleSelect: (i) => {
        state.tab = sections[i].title
        save()
        opts.repaint()
      },
    })
    return [...head, strip, ...sections[active].rows]
  }
  /*
  ONE OPEN AT A TIME ('fold'), as an accordion: opening a section closes the
  rest, and all of them may be closed. A panel's sections are long, so two
  open push the second's rows out of reach. 'folds' lets several stay open.
  */
  const single = opts.mode !== 'folds'
  const titles: string[] = []
  const out: Widget3d[] = []
  let showing = true
  let first = true
  let anyOpen = false
  for (const row of rows) {
    const sec = (row as any).section as Sec | undefined
    if (sec == null) {
      if (showing) out.push(row)
      continue
    }
    titles.push(sec.title)
    let isOpen = state.open.get(sec.title) ?? sec.open ?? first
    // Saved or declared, only the first open section stays open.
    if (single && anyOpen) isOpen = false
    anyOpen ||= isOpen
    first = false
    sec.isOpen = isOpen
    sec.toggle = () => {
      if (single && !isOpen) for (const t of titles) state.open.set(t, false)
      state.open.set(sec.title, !isOpen)
      save()
      opts.repaint()
    }
    out.push(row)
    showing = isOpen
  }
  return out
}

const sectionStates = new Map<
  string,
  { open: Map<string, boolean>; tab: string | null }
>()
function sectionState(key = ''): {
  open: Map<string, boolean>
  tab: string | null
} {
  let st = sectionStates.get(key)
  if (st == null) {
    st = { open: new Map(), tab: null }
    if (key !== '') {
      try {
        for (const [k, v] of Object.entries(
          JSON.parse(localStorage.getItem(key) || '{}')
        ))
          st.open.set(k, !!v)
        st.tab = localStorage.getItem(`${key}:tab`)
      } catch {
        /* no storage: defaults */
      }
    }
    sectionStates.set(key, st)
  }
  return st
}

/** What a panel offers the widgets inside it. */
/**
 * A host translated by a child's offset inside its container.
 *
 * A widget anchors its popup in ITS OWN coordinates — `select3d` uses `y: 0`
 * meaning "the top of my row" — and `panel3d` translates that by the child's
 * offset before passing it on. A nested container has to do the same hop, or
 * the popup lands at the top of the CONTAINER instead of beside the control
 * that opened it, which is what a light editor's preset menu did.
 *
 * `offsetOf` is called at popup time, not at wiring time, because the offsets
 * come from layout and layout happens later — and again on every resize.
 */
export function offsetHost(
  host: WidgetHost,
  offsetOf: () => { x: number; y: number }
): WidgetHost {
  return {
    ...host,
    showTip:
      host.showTip == null
        ? undefined
        : (t, anchor) => {
            const d = offsetOf()
            host.showTip!(t, {
              ...anchor,
              x: anchor.x + d.x,
              y: anchor.y + d.y,
            })
          },
    showPopup(config, ...items) {
      const d = offsetOf()
      return host.showPopup(
        {
          ...config,
          anchor: {
            ...config.anchor,
            x: config.anchor.x + d.x,
            y: config.anchor.y + d.y,
          },
        },
        ...items
      )
    },
  }
}

export interface WidgetHost {
  /**
   * Open a popup ABOVE the panel's content and mount it, returning a closer.
   *
   * Mounting is done here rather than left to the caller — the general
   * `panel.openPopup` deliberately does not mount, because a free-floating popup
   * differs flat (a positioned sibling) from in-scene (another plane). This one
   * can, because it is capped to the panel's own bounds: it lands inside the
   * panel's viewBox by construction, which is identical in both presentations.
   * The cost is that it cannot exceed the panel, which for a dropdown is the
   * right trade — and `maxHeight` makes a long list scroll rather than overflow.
   */
  showPopup: (
    config: {
      anchor: { x: number; y: number; width: number; height: number }
      side?: PopupSide
      width?: number
      maxHeight?: number
      /** Called when this popup goes away — including dismissal from outside. */
      handleClose?: () => void
    },
    ...items: Widget3d[]
  ) => { close: () => void }
  /** Close whatever popup is open, if any. */
  closePopup: () => void
  /**
   * Show a tooltip for `anchor` (widget-local, like a popup's), drawn by the
   * panel over its body so a bottom row's tip is not clipped, and flipped
   * above when there is no room below. For an icon-only control: a texture
   * shows no `<title>` and a headset has no browser tooltip (#112).
   */
  showTip?: (
    text: string,
    anchor: { x: number; y: number; width: number; height: number }
  ) => void
  /** Hide the tooltip. */
  hideTip?: () => void
  /** Ask the panel to re-run layout — a widget that changed height needs this. */
  relayout: () => void
  /**
   * The panel's inner size, and this widget's top within it.
   *
   * A widget cannot otherwise tell whether what it wants to open will FIT. The
   * keyboard needed exactly this: `showPopup` caps to the panel's bounds, so on
   * a short panel it produced a keyboard squeezed flat and placed over the
   * field. Without these it had no way to decline.
   */
  readonly bounds: { width: number; height: number }
  readonly top: number
  /**
   * Is `showLayer` a REAL layer, or will it fall back to a bounded popup?
   *
   * A caller that can be refused needs to know which it is getting. The keyboard
   * declines rather than squeezing itself into a short panel — but that check is
   * pointless, and wrong, when an unbounded plane is available.
   */
  readonly hasLayer: boolean
  /**
   * Open something in a layer ABOVE the panel, unbounded by it.
   *
   * `showPopup` mounts inside the panel's own viewBox, which is right for a
   * dropdown — it is guaranteed to fit and it rasterises identically flat and
   * in-scene. It is wrong for anything BIGGER than the panel. A keyboard is
   * bigger than most panels: on a 64px panel it came out 64px tall and sat on
   * the field it types into.
   *
   * Only something ABOVE the panel can provide a real layer, because mounting is
   * what differs — flat it is a positioned sibling, in a scene it is another
   * plane. `panelScene` installs one (an `openPopup` plane on the B3d); a bare
   * `panel3d` has nothing above it, so this **falls back to `showPopup`** and the
   * caller must still cope with being refused.
   */
  /**
   * A popup that must NOT escape its panel — the fallback `showPopup` uses when
   * there is no layer, exposed for the rare caller that wants it deliberately.
   */
  boundedPopup?: (
    config: {
      anchor: { x: number; y: number; width: number; height: number }
      side?: PopupSide
      width?: number
      maxHeight?: number
    },
    ...items: Widget3d[]
  ) => { close: () => void }
  showLayer: (
    config: {
      anchor: { x: number; y: number; width: number; height: number }
      side?: PopupSide
      width?: number
      maxHeight?: number
      /**
       * Give it a title band with move and close glyphs.
       *
       * For a popup you might want to KEEP — a readout, a panel of extra
       * information. A menu wants none: it is transient, you pick from it and
       * it goes, and a title bar on a dropdown is both odd and 30px of waste.
       *
       * The in-scene presentation draws handles regardless (the plane always
       * has them), so this decides the flat one and whether the sheet reserves
       * room at all.
       */
      chrome?: boolean
      /**
       * A caption for the title band, so two popups open at once can be
       * told apart. Only drawn where there is a band (`chrome`).
       */
      title?: string
      /** Called when it goes away, however it went. */
      handleClose?: () => void
    },
    ...items: Widget3d[]
  ) => { close: () => void }
}

/**
 * Set an inline CSS string and return the element. svgElements types `style`
 * as a style object, not a string, so we apply it via setAttribute instead.
 */
function css<T extends Element>(el: T, style: string): T {
  el.setAttribute('style', style)
  return el
}

// --- Reactive binding ------------------------------------------------------

interface Bound<T> {
  get(): T
  set(v: T): void
  /** Watch the value; returns the unsubscribe (#100). */
  subscribe(cb: () => void): () => void
}

/**
 * A widget value: a plain `T`, or a tosijs reactive box of one (a
 * `BoxedScalar<T>` — anything with `.value` and `.observe`). Passing the BOX is
 * the supported, two-way usage: the control moves the value and the value moves
 * the control. Typed structurally so this module needs no tosijs types, and so
 * a correct call no longer fails the typecheck (tosijs-3d#76 — 24 false errors
 * in one consumer panel, and every cast to silence them also silenced `min`).
 */
export type Bindable<T> =
  | T
  | {
      value: T
      observe(cb: (...args: any[]) => void): unknown
    }

/**
 * Wrap a value that may be a plain T or a tosijs reactive proxy. Proxies are
 * read/written through tosi and re-render on external change; plain values are
 * held locally. Either way `onChange` fires on set.
 */
function boundValue<T>(value: unknown, onChange?: (v: T) => void): Bound<T> {
  // A tosijs reactive leaf is a boxed proxy: an object exposing `.value`
  // (get/set) and `.observe(cb)`. A plain number/boolean is not, and falls
  // through to a local copy.
  const box = value as {
    value: T
    observe?: (cb: () => void) => void
  } | null
  if (box && typeof box === 'object' && typeof box.observe === 'function') {
    return {
      get: () => box.value,
      set: (v: T) => {
        box.value = v
        onChange?.(v)
      },
      /*
      THE UNSUBSCRIBE IS KEPT (#100). It used to be thrown away, so every
      widget bound to a box left a listener in tosijs's module-level list,
      and its closure held the widget's element, its panel and the host:
      ensemble measured +125 listeners and +5,360 nodes per round of panel
      rebuilds. The widget's `dispose` calls it; whoever drops a widget list
      disposes it (B3d does, and `panel3d`'s `dispose` does for its rows).
      Not reaped by `isConnected`: an in-scene panel's SVG is never in the
      document, and would stop updating while visible.
      */
      subscribe: (cb) => {
        const stop = box.observe!(cb) as unknown
        let done = false
        return () => {
          if (done) return
          done = true
          if (typeof stop === 'function') stop()
        }
      },
    }
  }
  let local = value as T
  return {
    get: () => local,
    set: (v: T) => {
      local = v
      onChange?.(v)
    },
    subscribe: () => () => {},
  }
}

/** Map a client point into an element's local user space (CTM inverse). */
function localPoint(el: SVGElement, clientX: number, clientY: number) {
  // For the root <svg>, ownerSVGElement is null — use the element itself (it IS
  // an SVGSVGElement and owns createSVGPoint). Getting this wrong leaves the
  // panel's overlay routing with raw screen coords, hit-testing nothing.
  const owner = (el.ownerSVGElement ?? el) as SVGSVGElement
  const ctm = (el as SVGGraphicsElement).getScreenCTM()
  if (!ctm || typeof owner.createSVGPoint !== 'function') {
    return { x: clientX, y: clientY }
  }
  const pt = owner.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  const p = pt.matrixTransform(ctm.inverse())
  return { x: p.x, y: p.y }
}

const baseText = (content: string, fill = TH.TEXT, bold = false) =>
  text(
    {
      'dominant-baseline': 'middle',
      'font-size': TH.FONT,
      'font-family': TH.FONT_FAMILY,
      'font-weight': bold ? TH.HEADING_WEIGHT : TH.TEXT_WEIGHT,
      fill,
    },
    content
  )

// --- Widgets ---------------------------------------------------------------

/**
 * A static caption row. `color` overrides the default text colour (e.g. an
 * accent heading); `bold` renders it bold; `muted` dims it (ignored if `color`
 * is set). `compact` shrinks the row to one text line instead of a full
 * interactive-height TH.ROW — for dense readouts (debug panels) where a 40px row per
 * short line is mostly wasted space.
 */
/**
 * **Lay widgets side by side on one row.**
 *
 * A panel only stacks, so a label-and-field pair costs two rows and eight
 * fields become sixteen rows of mostly whitespace — the ensemble editor's
 * report (tosijs-3d#37, item 5). A row is the missing axis.
 *
 * `weights` are proportional shares of the space left after the gaps, so
 * `weights: [1, 2]` is the usual label/field split. Children are middle-aligned
 * by default: the common case is a short label beside a taller control, and
 * top-aligning those makes the label look detached from what it names.
 *
 * **Pointer routing is by column, and it delegates in the child's OWN
 * coordinates** — a widget cannot know it has been put in a row, so it must
 * still receive `(0,0)` at its own top-left. Hit-testing follows the same
 * path, which is what keeps "grab between the controls to scroll" working
 * inside a row as well as outside it.
 */
export function row3d(
  config: {
    gap?: number
    /** Proportional shares of the post-gap space. Omit for equal columns. */
    weights?: number[]
    align?: 'top' | 'middle' | 'bottom'
  },
  ...children: Widget3d[]
): Widget3d {
  const gap = config.gap ?? TH.GAP
  const align = config.align ?? 'middle'
  const el = g()
  const wraps = children.map((c) => {
    const wrap = g()
    wrap.appendChild(c.el)
    el.appendChild(wrap)
    return wrap
  })
  // Column geometry from the last layout, so pointer routing uses exactly what
  // was drawn rather than recomputing and risking a disagreement.
  let cols: RowColumn[] = []
  let tops: number[] = []
  let rowHeight = 0

  const at = (x: number, y: number) => {
    for (let i = 0; i < cols.length; i++) {
      const c = cols[i]
      if (x >= c.x && x <= c.x + c.width) {
        return { i, lx: x - c.x, ly: y - tops[i] }
      }
    }
    return null
  }

  return {
    el,
    /*
    PASS THE HOST DOWN. A row is a container, and a control inside it that
    opens a popup needs the panel's host to do it. Without this a `select3d`
    in a row had no host, and its fallback for "no menu possible" is to STEP to
    the next option: tapping Land and Sky's world picker jumped straight from
    Earth to Mars, in a headset, rebuilding the planet before you could react.
    Offset per column, so the menu drops from the control and not from the
    row's left edge.
    */
    setHost(h) {
      children.forEach((c, i) =>
        c.setHost?.(
          offsetHost(h, () => ({ x: cols[i]?.x ?? 0, y: tops[i] ?? 0 }))
        )
      )
    },
    layout(width: number) {
      cols = rowColumns(width, children.length, gap, config.weights)
      const heights = children.map((c, i) => c.layout(cols[i].width))
      rowHeight = heights.length ? Math.max(...heights) : 0
      tops = heights.map((h) => alignOffset(rowHeight, h, align))
      wraps.forEach((wrap, i) => {
        wrap.setAttribute('transform', `translate(${cols[i].x}, ${tops[i]})`)
      })
      return rowHeight
    },
    handle(kind, x, y) {
      const hit = at(x, y)
      if (hit == null) return
      children[hit.i].handle?.(kind, hit.lx, hit.ly)
    },
    hitTest(x, y) {
      const hit = at(x, y)
      if (hit == null) return false
      const child = children[hit.i]
      // No hitTest means "the whole row is the control" for that child — the
      // same convention the panel uses one level up.
      return child.hitTest
        ? child.hitTest(hit.lx, hit.ly)
        : child.handle != null
    },
  }
}

export function label3d(config: {
  text: string
  muted?: boolean
  bold?: boolean
  color?: string
  compact?: boolean
  /**
   * A SECTION HEADER: tap it to fold away everything below it, up to the next
   * collapsible label (board #2466). The host panel (a `<tosi-b3d>`
   * `scenePanel`) does the folding and remembers what is open; the label only
   * draws its chevron and asks. A plain label is unchanged.
   */
  collapsible?: boolean
  /** Whether a collapsible section starts open. Default: only the first. */
  open?: boolean
  /** An icon for the section: as TABS (`panelSections="tabs"`) it replaces
   * the caption, so the strip stays narrow and does not overlap. */
  icon?: string
}): Widget3d & {
  section?: {
    title: string
    icon?: string
    open?: boolean
    isOpen: boolean
    toggle?: () => void
  }
} {
  const fill = config.color ?? (config.muted ? TH.MUTED : TH.TEXT)
  if (config.collapsible) return sectionLabel(config, fill)
  const t = baseText(config.text, fill, config.bold)
  const h = config.compact ? TH.LINE_H : TH.ROW
  t.setAttribute('x', String(TH.PAD_X))
  t.setAttribute('y', String(h / 2))
  return {
    el: g({ 'data-w3d': 'label' }, t),
    /*
    A CAPTION TRUNCATES; PROSE WRAPS. This one is a single line by definition —
    `text3d`/`textBlock3d` exist for anything that should flow — so a caption
    too long for the panel used to simply run off the right-hand edge and under
    the scroll rail, which is how the panel's own title read in a 282px VR
    panel.

    `layout` was already handed the width and threw it away.
    */
    layout(width) {
      t.textContent = ellipsize(
        config.text,
        width - TH.PAD_X * 2,
        config.bold ? TH.BOLD_FONT : TH.TEXT_FONT
      )
      return h
    },
  }
}

/**
 * A wrapped, multi-line text block — the honest way to render prose in an SVG
 * panel (NPC dialogue, a paragraph of help). Lines are broken by real glyph
 * measurement (`measureTextWrap`), so they neither clip nor waste space, and
 * explicit `\n`s are respected. The wrapping model is in `widgets3d-layout.ts`,
 * with its limits (whitespace breaks only; no bidi).
 */
export function text3d(config: { text: string; muted?: boolean }): Widget3d {
  return textBlock3d({ lines: [config.text], muted: config.muted })
}

/**
 * A compact, live-updatable stack of text lines, each wrapped to the panel width.
 *
 * This is the "text block" that replaces one-`label3d`-per-line for dense readouts:
 * compact line height (reclaims the vertical space) plus measured wrapping (kills the
 * clip). `update(lines)` re-lays-out at the last width it was given — so a live source
 * (a debug panel) can push new text every tick without a full panel rebuild, as long
 * as the line COUNT is stable (a changed count still needs a rebuild to reflow siblings).
 */
export function textBlock3d(config: {
  lines: string[]
  muted?: boolean
  bold?: boolean
  color?: string
}): Widget3d & { update(lines: string[]): void } {
  const el = g({ 'data-w3d': 'textblock' })
  const fill = config.color ?? (config.muted ? TH.MUTED : TH.TEXT)
  const font = config.bold ? TH.BOLD_FONT : TH.TEXT_FONT
  let lines = config.lines
  let lastWidth = 0

  const paint = (width: number): number => {
    while (el.firstChild) el.removeChild(el.firstChild)
    const wrapped: string[] = []
    for (const line of lines) {
      // Empty line stays empty (keeps blank-line rhythm); else wrap it.
      const w = measureTextWrap(line, width - TH.PAD_X * 2, font)
      for (const ln of w) wrapped.push(ln)
    }
    wrapped.forEach((ln, i) => {
      const t = baseText(ln, fill, config.bold)
      t.setAttribute('x', String(TH.PAD_X))
      t.setAttribute('y', String(PAD_Y + TH.LINE_H * (i + 0.6)))
      el.appendChild(t)
    })
    return Math.max(TH.LINE_H, wrapped.length * TH.LINE_H + PAD_Y * 2)
  }

  return {
    el,
    layout(width) {
      lastWidth = width
      return paint(width)
    },
    update(next) {
      lines = next
      if (lastWidth > 0) paint(lastWidth)
    },
  }
}

/** What a popup button's controls get: close the popup, or show others in its place. */
export interface PopupHandle {
  close(): void
  show(items: Widget3d[]): void
}

/** A pressable button. */
export function button3d(config: {
  label: string
  /** Fired on release, on the thing pressed. */
  handleClick?: () => void
  /**
   * Make this a MENU button: pressing it opens these actions anchored to the
   * button, instead of (not as well as) firing `handleClick`.
   *
   * Both would be a trap — a control that sometimes acts and sometimes opens
   * has no reliable meaning, and you find out which by pressing it.
   */
  menu?: MenuAction[]
  /**
   * Make this a POPUP button: pressing it opens these controls (a small form:
   * a name and OK, a confirm) in a popup anchored to the button, flat and in
   * a headset alike. It gets a handle to `close` the popup or `show` other
   * controls in its place (a form's next step), so a press inside it can.
   * Like `menu`, instead of `handleClick`.
   */
  popup?: (popup: PopupHandle) => Widget3d[]
}): Widget3d {
  const bg = rect({
    x: 0,
    y: 4,
    rx: 8,
    ry: 8,
    height: TH.ROW - 8,
    fill: TH.BTN_BG,
  })
  const lbl = baseText(config.label)
  lbl.setAttribute('text-anchor', 'middle')
  lbl.setAttribute('y', String(TH.ROW / 2))
  const el = css(g({ 'data-w3d': 'button' }, bg, lbl), 'cursor:pointer')
  let host: WidgetHost | null = null
  let btnWidth = 0
  let open: { close: () => void } | null = null
  // A press inside the popup may close it or replace its controls: done a
  // tick later, so the popup is not torn down while it is handling the press.
  const popup: PopupHandle = {
    close: () =>
      queueMicrotask(() => {
        const was = open
        open = null
        was?.close()
      }),
    show: (items) => queueMicrotask(() => showPopup(items)),
  }
  const showPopup = (items: Widget3d[]) => {
    const was = open
    open = null
    was?.close()
    if (host == null) return
    const handle = host.showPopup(
      {
        anchor: { x: 0, y: 0, width: btnWidth, height: TH.ROW },
        width: Math.max(btnWidth, 260),
        handleClose: () => {
          if (open === handle) open = null
        },
      },
      ...items
    )
    open = handle
  }
  return {
    el,
    setHost(h) {
      host = h
    },
    layout(width) {
      btnWidth = width
      bg.setAttribute('width', String(width))
      lbl.setAttribute('x', String(width / 2))
      // Centred text overflows BOTH ends, so it escapes the button on the left
      // as well — the one case where a caption spills outside its own control.
      lbl.textContent = ellipsize(
        config.label,
        width - TH.PAD_X * 2,
        TH.TEXT_FONT
      )
      return TH.ROW
    },
    handle(kind) {
      // The LABEL follows too — on a strongly-coloured active background the
      // ordinary text colour may not read, which is the whole reason
      // `buttonActiveText` is a separate token.
      if (kind === 'down') {
        bg.setAttribute('fill', TH.BTN_ACTIVE)
        lbl.setAttribute('fill', w3dTheme.buttonActiveText)
      } else if (kind === 'leave') {
        bg.setAttribute('fill', TH.BTN_BG)
        lbl.setAttribute('fill', TH.TEXT)
      } else {
        bg.setAttribute('fill', TH.BTN_HOVER) // hover / move / up
        lbl.setAttribute('fill', TH.TEXT)
        if (kind === 'up') {
          if (config.popup != null) {
            showPopup(config.popup(popup))
          } else if (config.menu != null && host != null) {
            openMenu3d(
              host,
              { x: 0, y: 0, width: btnWidth, height: TH.ROW },
              config.menu
            )
          } else {
            handlerOf<() => void>(config, 'handleClick', 'onClick')?.()
          }
        }
      }
    },
  }
}

/*
A TOOLTIP, drawn in the SVG: a texture shows no `<title>`, and a headset has
no browser tooltip at all, but a controller ray hovers as a mouse does. One
drawer, used by `iconBar3d` on its own and by `panel3d` for every widget in
it (`WidgetHost.showTip`), so a tip looks the same wherever it comes from.
Under its anchor, centred, kept inside `width`; above it when there is no
room below `height`.
*/
const TIP_FONT = 11
function tipDrawer(): {
  el: SVGElement
  show: (
    title: string,
    at: { x: number; y: number; width: number; height: number },
    width: number,
    height?: number
  ) => void
  hide: () => void
} {
  const tipText = css(
    text(
      {
        'dominant-baseline': 'middle',
        'font-size': TIP_FONT,
        'font-family': TH.FONT_FAMILY,
        'font-weight': TH.TEXT_WEIGHT,
        fill: TH.TEXT,
      },
      ''
    ),
    'pointer-events:none'
  )
  const tipBg = rect({
    height: TIP_FONT + 10,
    rx: 5,
    ry: 5,
    fill: TH.PANEL_BG,
    stroke: TH.MUTED,
    'stroke-width': 1,
  })
  const el = css(
    g({ visibility: 'hidden', 'data-w3d-tip': '' }, tipBg, tipText),
    'pointer-events:none'
  ) as unknown as SVGElement
  return {
    el,
    show(title, at, width, height = Infinity) {
      const w =
        measureTextWidth(title, {
          size: TIP_FONT,
          family: TH.FONT_FAMILY,
          weight: TH.TEXT_WEIGHT,
        }) + 12
      const h = TIP_FONT + 10
      const x = Math.max(
        0,
        Math.min(at.x + at.width / 2 - w / 2, Math.max(0, width - w))
      )
      const below = at.y + at.height + 3
      const y = below + h > height ? Math.max(0, at.y - 3 - h) : below
      tipBg.setAttribute('x', String(x))
      tipBg.setAttribute('y', String(y))
      tipBg.setAttribute('width', String(w))
      tipText.setAttribute('x', String(x + 6))
      tipText.setAttribute('y', String(y + h / 2 + 0.5))
      tipText.textContent = title
      el.setAttribute('visibility', 'visible')
    },
    hide() {
      el.setAttribute('visibility', 'hidden')
    },
  }
}

/**
 * A horizontal strip of icon toggle-buttons — a compact toolbar for a panel
 * header. Each item is an [[svg-icons|iconGlyph]] (explicit colours, so it
 * rasterizes onto the in-scene / XR texture the same as it draws flat), sized to
 * a square button; `active` items get a selected background and an accent
 * underline. Left-aligned, so the empty right end reads as scroll-drag surface
 * (via `hitTest`) — important in VR where a precise point is hard.
 *
 * Used to reduce a stack of debug sections to one icon apiece: the scene panel
 * collapses Perf Stats / each debug source to an icon here, and expands the
 * matching content below the bar when its icon is on.
 */
export function iconBar3d(config: {
  items: Array<{
    icon: string
    title?: string
    active?: boolean
    /**
     * Nothing to report — drawn faint.
     *
     * Distinct from `active` (which is a SELECTION) and from disabled (which
     * this deliberately is not): a dimmed icon is still pressable, because
     * "confirm there are no errors" is a thing people do.
     */
    dim?: boolean
    /** Fired on release, on the thing pressed. */
    handleClick?: () => void
  }>
}): Widget3d {
  const BS = 32 // button size
  // Tighter than the panel gap on purpose — an icon bar reads as one control.
  const ICON_GAP = 6
  const ICON = 20
  /*
  SELECTED IS NOT PRESSED.

  This used `buttonActive` for the selected item, which is the PRESS colour — so
  a selected icon looked permanently pressed, and pressing one showed nothing
  new because it was already wearing the press. Tonio: "the buttons in the
  iconButtonBar don't color like buttons (e.g. active)."

  Three states, three tokens, in the order they escalate: `buttonBg` at rest,
  `buttonHover` under the pointer, `buttonActive` while held — and `selectedBg`
  for "this one is on", which is a different axis entirely and is why the theme
  has a separate token for it. See UI-DESIGN-NOTES: selection must not compete
  with hover and focus for intensity.
  */
  const SELECTED = w3dTheme.selectedBg
  /*
  ITS OWN ROW HEIGHT, not a control row's. A bar of 32px buttons in a 46px row
  left 7px above and below, and under a title bar that read as a band of empty
  panel. Tonio: "space is at a premium here and there's a lot of padding above
  and likely too much below that toolbar."
  */
  const BAR_ROW = BS + 4
  const by = (BAR_ROW - BS) / 2
  // Per-item background + accent underline. The glyph itself bakes its colour at
  // creation (texture-safe), so state is shown by the background, not the icon.
  const cells = config.items.map((item) => {
    const bg = rect({
      x: 0,
      y: 0,
      width: BS,
      height: BS,
      rx: 8,
      ry: 8,
      fill: item.active ? SELECTED : TH.BTN_BG,
    })
    const underline = rect({
      x: 6,
      y: BS - 3,
      width: BS - 12,
      height: 2,
      rx: 1,
      fill: item.active ? TH.ACCENT : 'transparent',
    })
    /*
    DIM IS A COLOUR, not an opacity.

    This SVG is rasterised to a texture where a group opacity composites against
    whatever is behind it in the same pass — which for an icon sitting on its
    own button chrome reads as a smudge rather than as faintness. Muting the
    glyph's colour says the same thing and survives the raster.
    */
    const glyph = iconGlyph(item.icon, {
      // Baked at creation (texture-safe), so a SELECTED icon takes the active
      // label colour here rather than being repainted later.
      color: item.active
        ? w3dTheme.buttonActiveText
        : item.dim
        ? TH.MUTED
        : TH.TEXT,
      size: ICON,
      x: (BS - ICON) / 2,
      y: (BS - ICON) / 2,
    })
    const cell = css(
      g({ 'data-w3d-icon': item.icon }, bg, glyph, underline),
      'cursor:pointer'
    )
    // A real tooltip — but ONLY in live DOM. The XR panel rasterizes this SVG
    // to a texture, where <title> renders nothing at all, so see the caption
    // below: in a headset an icon with a hidden tooltip is just a mystery.
    if (item.title) cell.appendChild(svgElements.title(item.title))
    return { item, bg, cell, x: 0, y: 0 }
  })
  /*
  A TOOLTIP, drawn in the SVG, in place of the captions that used to sit
  under every icon.

  The captions answered a real question from the headset ("What is the
  'compass' icon supposed to be toggling?" — it was Re-seat), but they cost
  every bar a band of tiny first-words, and space is what a panel has least
  of. Tonio: "I think the icon button captions can be tooltips." A hover
  answers the same question: a controller ray hovers as a mouse does, and
  a texture shows this where it would show no `<title>`. Full text now,
  not the first word, because it has room.
  */
  const tip = tipDrawer()
  let host: WidgetHost | null = null
  const el = g(
    { 'data-w3d': 'iconbar' },
    ...cells.map((c) => c.cell),
    // Last, so it draws over the cells beside and below it.
    tip.el
  )
  let barW = 0
  const step = BS + ICON_GAP
  /*
  IT WRAPS, because it used to run off the edge instead.

  `layout()` ignored the width it was handed and placed every cell at `i * step`
  on one line. A scene's bar is Exit VR + Re-seat + Perf Stats + one per
  registered debug source + the gadgets — ten items on the terrain demo, 380px
  of them inside a 296px panel — so the last few were painted outside the panel
  and their captions collided on the way out.

  Wrapping rather than scrolling or truncating: these are the controls you reach
  for once a session, and one that is off-screen is one you cannot press. A
  second row costs 44px of a panel; a missing Exit VR costs the session.
  */
  let perRow = cells.length
  const rowH = BAR_ROW
  const indexAt = (x: number, y = 0): number => {
    const col = Math.floor(x / step)
    const row = Math.max(0, Math.floor(y / rowH))
    if (col < 0 || col >= perRow) return -1
    const i = row * perRow + col
    if (i < 0 || i >= cells.length) return -1
    // Reject the ICON_GAP dead-zone between buttons.
    return x - col * step <= BS ? i : -1
  }
  let pressed = -1
  const paint = (hover: number) => {
    cells.forEach((c, i) => {
      const base = c.item.active ? SELECTED : TH.BTN_BG
      const fill =
        i === pressed ? TH.BTN_ACTIVE : i === hover ? TH.BTN_HOVER : base
      c.bg.setAttribute('fill', fill)
    })
    const c = cells[hover]
    const title = c?.item.title
    // In a panel, the panel's tip: drawn over its body, so a bottom row's tip
    // is not cut off by the clip. Alone, its own.
    if (host?.showTip != null) {
      if (title) host.showTip(title, { x: c.x, y: c.y, width: BS, height: BS })
      else host.hideTip?.()
      return
    }
    if (!title) {
      tip.hide()
      return
    }
    tip.show(title, { x: c.x, y: c.y, width: BS, height: BS }, barW)
  }
  return {
    el,
    setHost(h: WidgetHost) {
      host = h
    },
    layout(width: number) {
      barW = width
      // `+ ICON_GAP` because the last cell in a row needs no trailing gap.
      perRow = Math.max(1, Math.floor(((width || 0) + ICON_GAP || step) / step))
      cells.forEach((c, i) => {
        const col = i % perRow
        const row = Math.floor(i / perRow)
        c.x = col * step
        c.y = row * rowH + by
        c.cell.setAttribute('transform', `translate(${c.x} ${c.y})`)
      })
      return Math.ceil(cells.length / perRow) * rowH
    },
    hitTest(x, y) {
      return indexAt(x, y) >= 0
    },
    handle(kind, x, y) {
      if (kind === 'leave') {
        pressed = -1
        paint(-1)
        return
      }
      // `y` matters now that the bar can wrap onto a second row.
      const i = indexAt(x, y)
      if (kind === 'down') pressed = i
      // Release BEFORE firing, so a handler that rebuilds the panel does not
      // leave a button stuck looking held.
      const fired = kind === 'up' && i >= 0 && i === pressed
      if (kind === 'up') pressed = -1
      paint(i)
      if (fired) {
        const it = cells[i].item as Record<string, unknown>
        handlerOf<() => void>(it, 'handleClick', 'onClick')?.()
      }
    },
  }
}

/** A labelled on/off switch bound to a boolean. */
export function toggle3d(config: {
  label: string
  value: Bindable<boolean>
  /** Fired as the value changes. */
  handleChange?: (v: boolean) => void
}): Widget3d {
  const bound = boundValue<boolean>(
    config.value,
    handlerOf<(v: boolean) => void>(config, 'handleChange', 'onChange')
  )
  const lbl = baseText(config.label)
  lbl.setAttribute('x', String(TH.PAD_X))
  lbl.setAttribute('y', String(TH.ROW / 2))
  const trackW = 46
  const trackH = 24
  const knobR = 9
  const track = rect({
    y: (TH.ROW - trackH) / 2,
    width: trackW,
    height: trackH,
    rx: trackH / 2,
    ry: trackH / 2,
    fill: TH.TRACK,
  })
  const knob = circle({ cy: TH.ROW / 2, r: knobR, fill: '#fff' })
  const rowBg = rect({
    x: 0,
    y: 2,
    height: TH.ROW - 4,
    rx: 6,
    fill: 'transparent',
  })
  const el = css(
    g({ 'data-w3d': 'toggle' }, rowBg, lbl, track, knob),
    'cursor:pointer'
  )
  let trackX = 0
  const reflect = () => {
    const on = bound.get()
    track.setAttribute('fill', on ? TH.ACCENT : TH.TRACK)
    knob.setAttribute(
      'cx',
      String(on ? trackX + trackW - knobR - 3 : trackX + knobR + 3)
    )
  }
  const flip = () => {
    bound.set(!bound.get())
    reflect()
  }
  const unbind = bound.subscribe(reflect)
  return {
    el,
    dispose: unbind,
    layout(width) {
      rowBg.setAttribute('width', String(width))
      trackX = width - trackW - TH.PAD_X
      track.setAttribute('x', String(trackX))
      // Up to the switch, not to the panel edge: an untruncated label ran
      // straight under it and out the other side.
      lbl.textContent = ellipsize(
        config.label ?? '',
        trackX - TH.PAD_X - 8,
        TH.TEXT_FONT
      )
      reflect()
      return TH.ROW
    },
    // Only the switch is interactive; the label area is scroll surface.
    hitTest(x) {
      return x >= trackX - 8 && x <= trackX + trackW + 8
    },
    handle(kind) {
      if (kind === 'leave') rowBg.setAttribute('fill', 'transparent')
      else {
        rowBg.setAttribute('fill', TH.ROW_HOVER)
        if (kind === 'up') flip()
      }
    },
  }
}

/** A horizontal slider bound to a number in [min, max], optionally stepped. */
export function slider3d(config: {
  label?: string
  value: Bindable<number>
  min?: number
  max?: number
  /** On a `log` scale this is in DECADES, not units. */
  step?: number
  /**
   * Snap the VALUE to a multiple of this, after the scale is applied.
   *
   * Different question from `step`, and both are useful: `step` is how far one
   * notch moves you (in the scale's units — octaves on `log2`); `snap` is which
   * values are legal at all. `scale: 'log2', snap: 1` gives a grid size that is
   * always a whole number but still easy to set at both ends of its range.
   */
  snap?: number
  /**
   * `linear` (default), `log`, or `log2`.
   *
   * `log` and `log2` place the handle identically — the base cancels in the
   * mapping — and differ only in what one `step` means: a decade versus an
   * octave. Use `log2` when the meaningful values double (grid sizes, texture
   * sizes); `log` when they scale by orders of magnitude.
   *
   * Reach for `log` when the range spans orders of magnitude — a light's
   * intensity, a frequency, a scale factor. On a linear 0..1000 track every
   * value below 1 lives in the first thousandth of the travel, so the numbers
   * people actually want are the ones they cannot set.
   *
   * Requires `min > 0`; a range including zero falls back to linear rather
   * than producing NaN.
   */
  scale?: SliderScale
  /**
   * Put an explicit ZERO at the bottom of a log track.
   *
   * A log scale cannot represent zero, but plenty of decade-spanning
   * quantities have an explicit off — a sky's `realtimeScale` (0 is a still
   * sky, and it is the DEFAULT), fog density, wind speed, any rate. Without
   * this the default becomes unreachable the moment you touch the control,
   * which is worse than the cramped linear track it replaced.
   *
   * `min` then means the log FLOOR — the smallest non-zero value — rather than
   * the bottom of the travel. The handle catches at zero rather than
   * approaching it asymptotically.
   */
  zeroStop?: boolean
  /**
   * SIGNIFICANT digits kept on a log track. Default 4.
   *
   * Exponentiating a float leaves noise — a handle that looks like it is on
   * 0.015 produces 0.014999999999999999, which is harmless on screen and not
   * harmless in a document a consumer serialises and diffs.
   *
   * Significant digits rather than decimal places, because this is the control
   * that spans decades: four DECIMALS would round 0.0001234 to 0.0001 and
   * anything smaller to zero, destroying the end of the range a log scale is
   * there to reach. Four significant digits keeps 0.0001234 whole.
   *
   * Ignored on a linear track, where `step` and `snap` already say it.
   */
  precision?: number
  /** Fired as the value changes. */
  handleChange?: (v: number) => void
  /**
   * Where the number lives.
   *
   * - `'peek'` (default) — shown at the right, BESIDE the label, while you
   *   point at or drag it; the label stays. Right for a HUD or a settings
   *   panel, where the label matters more than the digits and space is tight.
   * - `'always'` — a permanent right-hand readout, with the track shortened to
   *   make room. Right for anything you have to READ rather than just set:
   *   ensemble's coordinates were unreadable because a handle position is not a
   *   number (tosijs-3d#37, item 3).
   * - `'never'` — no readout at all.
   */
  showValue?: 'peek' | 'always' | 'never'
  /** Format the readout — units, precision, anything. Defaults to step-derived decimals. */
  format?: (v: number) => string
  /**
   * `[lo, hi]` — where the values anybody wants live, drawn as a soft band
   * behind the track. SOFT bounds: the handle still reaches `min` and `max`.
   *
   * The landing spot for a schema's `x-useful` (tosijs-3d#83). Narrowing
   * `min`/`max` to the band instead would make a documented range
   * unreachable, which is a decision a panel should not make on the schema's
   * behalf. Clamped to the track; ignored if it is empty or inverted.
   */
  useful?: [number, number]
  /**
   * Named values on the track: a tick at each, a detent that catches the
   * handle when you pass within a few pixels, and the stop's name beside the
   * number while the value is on it ("0.5 semi-active").
   *
   * The landing spot for a schema's `x-stops` (tosijs-3d#121): a volcano's
   * activity is a continuous dial, and also has three values worth naming.
   * The handle still reaches everything between them.
   */
  stops?: Array<{ value: number; label?: string }>
}): Widget3d {
  const min = config.min ?? 0
  const max = config.max ?? 1
  const step = config.step ?? 0
  const scale: SliderScale = config.scale ?? 'linear'
  const snap = config.snap ?? 0
  const zeroStop = config.zeroStop ?? false
  const precision = config.precision ?? DEFAULT_SLIDER_PRECISION
  const stops = (config.stops ?? []).filter(
    (s) =>
      Number.isFinite(s.value) &&
      Number.isFinite(valueToFraction(s.value, min, max, scale, zeroStop))
  )
  const bound = boundValue<number>(
    config.value,
    handlerOf<(v: number) => void>(config, 'handleChange', 'onChange')
  )
  const labelText = config.label ?? ''
  const lbl = config.label ? baseText(config.label) : null
  // A clip, so a label squeezed by the track's minimum is TRUNCATED rather than
  // painted over the control it just gave way to.
  const labelClipId = `w3d-lbl-${clipSeq++}`
  const labelClip = rect({ x: 0, y: 0, width: 0, height: TH.ROW })
  const labelClipPath = clipPath({ id: labelClipId }, labelClip)
  /*
  TWO ROWS: the caption above, the track across the FULL WIDTH below.

  It used to be one row — label, track, value — and the track got whatever was
  left, which on a narrow panel was not much. `MIN_TRACK` bought it a floor at
  the label's expense, but that is rationing rather than a fix: the row is only
  ever as wide as the panel, and three things wanted it.

  Tonio: "the slider is the full width and the displayed number is above it.
  The narrow width of the slider makes it harder to use." Stacking gives the
  track the whole width, which matters most exactly where the old layout hurt
  most — a headset, where you aim with your arm and the panel is 282px across.

  The caption is a notch smaller than body text, so two rows cost less than one
  and a half. A slider is now taller than a toggle, and it should be: it is the
  control that needs the most aim.
  */
  const CAP_Y = Math.round(TH.FONT * 1.05)
  const TRACK_Y = CAP_Y + Math.round(TH.FONT * 0.85)
  const SLIDER_H = TRACK_Y + Math.round(TH.FONT * 0.8)
  const capFont: FontSpec = { ...TH.TEXT_FONT, size: TH.FONT * 0.86 }
  if (lbl) {
    lbl.setAttribute('x', String(TH.PAD_X))
    lbl.setAttribute('y', String(CAP_Y))
    lbl.setAttribute('font-size', String(capFont.size))
  }
  const trackEl = rect({
    height: 6,
    rx: 3,
    ry: 3,
    fill: TH.TRACK,
    y: TRACK_Y - 3,
  })
  const fillEl = rect({
    height: 6,
    rx: 3,
    ry: 3,
    fill: TH.ACCENT,
    y: TRACK_Y - 3,
  })
  const knob = circle({ cy: TRACK_Y, r: 10, fill: '#fff' })
  // Taller than the track so it still reads where the fill covers it.
  const usefulEl = config.useful
    ? rect({
        height: 12,
        rx: 6,
        ry: 6,
        fill: TH.ACCENT,
        'fill-opacity': 0.22,
        y: TRACK_Y - 6,
        'data-part': 'useful',
      })
    : null
  // Exact-value readout: shown (in place of the track) while you point at or drag
  // the slider, so the precise number is legible even at low XR texture res. The
  // label stays visible beside it. Decimals follow the step.
  const decimals =
    step > 0 ? (step < 1 ? Math.min(4, -Math.floor(Math.log10(step))) : 0) : 2
  const showValue = config.showValue ?? 'peek'
  /*
  A LOG slider formats by significant figures, not by decimals.

  Decimals come from `step`, which on a log scale is in decades — so the linear
  rule would print "1000.00" and "0.01" from the same setting, padding the big
  end with noise while barely resolving the small one. Three significant
  figures reads correctly across the whole range, which is the point of using
  the scale at all.
  */
  const logFormat = (v: number) =>
    v === 0
      ? '0'
      : Math.abs(v) >= 100
      ? String(Math.round(v))
      : String(Number(v.toPrecision(3)))
  const formatNumber =
    config.format ??
    (scale === 'log' ? logFormat : (v: number) => v.toFixed(decimals))
  const onStop = (v: number) =>
    stops.find(
      (s) =>
        s.label &&
        Math.abs(v - s.value) <= 1e-9 * Math.max(1, Math.abs(s.value))
    )
  const format = (v: number) => {
    const s = onStop(v)
    return s ? `${formatNumber(v)} ${s.label}` : formatNumber(v)
  }
  const stopTicks = stops.map(() =>
    rect({
      width: 2,
      height: 12,
      y: TRACK_Y - 6,
      fill: TH.TEXT,
      'fill-opacity': 0.45,
      'data-part': 'stop',
    })
  )
  const valText = baseText('', TH.ACCENT)
  valText.setAttribute('text-anchor', 'end')
  valText.setAttribute('y', String(CAP_Y))
  valText.setAttribute('font-size', String(capFont.size))
  valText.setAttribute('font-weight', '600')
  valText.setAttribute('display', 'none')
  /*
  The ALWAYS readout is a separate element from the peek one, deliberately.

  Peek appears only while you touch it; always sits at the right with the
  track shortened to clear it. Trying to make one element do both means it
  moves when you touch it, which is the one thing a number you are reading must
  not do.
  */
  const fixedVal = baseText('', TH.ACCENT)
  fixedVal.setAttribute('text-anchor', 'end')
  fixedVal.setAttribute('y', String(CAP_Y))
  fixedVal.setAttribute('font-size', String(capFont.size))
  fixedVal.setAttribute('font-weight', '600')
  if (showValue !== 'always') fixedVal.setAttribute('display', 'none')
  /*
  Reserve the width of the WIDEST value it can show, not of the current one --
  measured at both ends of the range (and via `format`, so units and precision
  are included). Sizing to the current value makes the track resize as you drag
  it, which looks like the slider fighting you.
  */
  const readoutW =
    showValue === 'always'
      ? Math.ceil(
          Math.max(
            measureTextWidth(format(min), TH.TEXT_FONT),
            measureTextWidth(format(max), TH.TEXT_FONT),
            ...stops.map((s) => measureTextWidth(format(s.value), TH.TEXT_FONT))
          )
        ) + 10
      : 0
  const rowBg = rect({
    x: 0,
    y: 2,
    height: TH.ROW - 4,
    rx: 6,
    fill: 'transparent',
  })
  const el = css(
    g(
      { 'data-w3d': 'slider' },
      rowBg,
      ...(lbl ? [labelClipPath, lbl] : []),
      ...(usefulEl ? [usefulEl] : []),
      trackEl,
      fillEl,
      ...stopTicks,
      knob,
      valText,
      fixedVal
    ),
    'cursor:pointer'
  )
  let trackX = 0
  let trackW = 0
  const reflect = () => {
    // Coerce: a bound HTML <input> writes its value back as a STRING ("14"), and
    // an unresolved binding can be undefined — Number() handles both, falling
    // back to min so we never write cx="NaN".
    const raw = Number(bound.get())
    const v = Number.isNaN(raw) ? min : raw
    const f = valueToFraction(v, min, max, scale, zeroStop)
    const cx = trackX + f * trackW
    knob.setAttribute('cx', String(cx))
    fillEl.setAttribute('x', String(trackX))
    fillEl.setAttribute('width', String(Math.max(0, cx - trackX)))
    const shown = format(v)
    valText.textContent = shown
    fixedVal.textContent = shown
  }
  /*
  Peek appears BESIDE the label now rather than in place of it. Replacing it was
  a concession to a single row — there was nowhere else for a number to go — and
  a label that vanishes the moment you touch the control is exactly the wrong
  time to lose the name of the thing you are adjusting.
  */
  const peek = (on: boolean) => {
    if (showValue !== 'peek') return
    valText.setAttribute('display', on ? 'inline' : 'none')
  }
  // x is the widget-local SVG x — no CTM/clientX, so this works in-scene/VR too.
  const STOP_CATCH = 8 // px: the detent's reach either side of a stop
  const setFromX = (x: number) => {
    const caught = stops.find(
      (s) =>
        Math.abs(
          trackX +
            valueToFraction(s.value, min, max, scale, zeroStop) * trackW -
            x
        ) <= STOP_CATCH
    )
    const f = (x - trackX) / (trackW || 1)
    bound.set(
      caught
        ? caught.value
        : fractionToValue(f, min, max, step, scale, snap, zeroStop, precision)
    )
    reflect()
  }
  const unbind = bound.subscribe(reflect)
  return {
    el,
    dispose: unbind,
    layout(width) {
      rowBg.setAttribute('width', String(width))
      rowBg.setAttribute('height', String(SLIDER_H - 4))
      /*
      THE TRACK TAKES THE WHOLE WIDTH, because it is on its own row now and
      nothing competes with it.

      What this replaces was a negotiation — `MIN_TRACK` reserved 90px and the
      label was clipped to whatever remained — and the negotiation only existed
      because three things shared one row. Stacking removes the argument rather
      than arbitrating it.
      */
      trackX = TH.PAD_X + 10
      trackW = Math.max(20, width - trackX - TH.PAD_X - 10)
      trackEl.setAttribute('x', String(trackX))
      trackEl.setAttribute('width', String(trackW))
      stops.forEach((s, i) => {
        const f = valueToFraction(s.value, min, max, scale, zeroStop)
        stopTicks[i].setAttribute('x', String(trackX + f * trackW - 1))
      })
      if (usefulEl && config.useful) {
        const [lo, hi] = config.useful
        const f0 = Math.max(0, valueToFraction(lo, min, max, scale, zeroStop))
        const f1 = Math.min(1, valueToFraction(hi, min, max, scale, zeroStop))
        const ok = Number.isFinite(f0) && Number.isFinite(f1) && f1 > f0
        usefulEl.setAttribute('display', ok ? 'inline' : 'none')
        usefulEl.setAttribute('x', String(trackX + (ok ? f0 : 0) * trackW - 6))
        usefulEl.setAttribute('width', String(ok ? (f1 - f0) * trackW + 12 : 0))
      }
      fixedVal.setAttribute('x', String(width - TH.PAD_X))
      valText.setAttribute('x', String(width - TH.PAD_X))
      /*
      ELLIPSIS, NOT A CLIP. Tonio: "Can we make captions check the width of the
      string and truncate to ellipsis?" A clipped label ends mid-stroke and says
      nothing about having been cut — `cameraHeightOffset` becomes
      `cameraHeigh` with the h sliced down the middle, and nothing tells you it
      was not named that. An ellipsis is a claim that the name goes on, for the
      cost of one character.

      The caption yields to the readout, which is the same ranking the old
      single-row version settled on — a number you cannot read is a broken
      control, a name you can half-read is a nuisance — applied to text rather
      than to a track.
      */
      if (lbl) {
        const room = width - TH.PAD_X * 2 - readoutW - (readoutW > 0 ? 8 : 0)
        lbl.textContent = ellipsize(labelText, room, capFont)
      }
      reflect()
      return SLIDER_H
    },
    /*
    The whole width is the track now, so the whole width is interactive. The old
    test excluded the label area, which on a stacked layout is a different row —
    and a slider that ignores presses at its own left-hand end is precisely the
    bug the single-row layout made unavoidable.
    */
    hitTest(x) {
      return x >= trackX - 14 && x <= trackX + trackW + 14
    },
    handle(kind, x) {
      if (kind === 'leave') {
        rowBg.setAttribute('fill', 'transparent')
        peek(false)
      } else {
        rowBg.setAttribute('fill', TH.ROW_HOVER)
        peek(true) // pointing at it (hover/press) → show the exact value
        if (kind === 'down' || kind === 'move') setFromX(x) // hover/up don't set
      }
    },
  }
}

/**
 * A select: `label      value ⌄`. Tapping the value (the right-hand cluster) opens
 * a popup list of the options, anchored under it; picking one sets the value and
 * closes the list. The popup is its own surface, so a panel does not crop it.
 * Binds the selected value (string or number); `options` are bare values or
 * `{ label, value }` pairs.
 *
 * The list needs a host to open in. `panel3d` always gives it one (through a
 * `row3d` too), so in a panel this is a select and nothing else.
 * Only without a host — a bare widget with no panel — does a tap step to the
 * next option instead, wrapping around the ends unless `wrap: false`.
 */
export function select3d(config: {
  label?: string
  value: Bindable<string | number>
  options: Array<string | number | { label: string; value: string | number }>
  /** Only for the hostless step fallback: wrap past the ends (default true). */
  wrap?: boolean
  /**
   * Keep the menu open after a pick, until it is closed or something behind
   * it is pressed: for trying options one after another (a hairstyle, an
   * outfit) and watching each take effect.
   */
  stayOpen?: boolean
  /** Fired as the value changes. */
  handleChange?: (v: string | number) => void
}): Widget3d {
  const opts = config.options.map((o) =>
    o != null && typeof o === 'object'
      ? { label: o.label, value: o.value }
      : { label: String(o), value: o as string | number }
  )
  const wrap = config.wrap ?? true
  const bound = boundValue<string | number>(
    config.value,
    handlerOf<(v: string | number) => void>(config, 'handleChange', 'onChange')
  )

  const lbl = config.label ? baseText(config.label) : null
  if (lbl) {
    lbl.setAttribute('x', String(TH.PAD_X))
    lbl.setAttribute('y', String(TH.ROW / 2))
  }
  const val = baseText('')
  val.setAttribute('text-anchor', 'end')
  val.setAttribute('y', String(TH.ROW / 2))
  // The affordance. `chevron90r` is the shipped right-chevron rotated a quarter
  // turn — the suffix system, rather than a second piece of art to keep in sync.
  /*
  WRAPPED, because the glyph's own transform is load-bearing.

  `iconGlyph` encodes placement AND the suffix's rotation in one `transform` on
  the element it returns, so setting `transform` to position it silently
  discards the quarter turn — the chevron then points RIGHT, which is the one
  direction that means something else. Position the wrapper; never touch the
  glyph.
  */
  const caret = g(
    {},
    iconGlyph('chevron90r', {
      size: 14,
      color: TH.ACCENT,
    }) as unknown as SVGElement
  )
  const rowBg = rect({
    x: 0,
    y: 2,
    height: TH.ROW - 4,
    rx: 6,
    fill: 'transparent',
  })
  const el = css(
    g({ 'data-w3d': 'select' }, rowBg, ...(lbl ? [lbl] : []), val, caret),
    'cursor:pointer'
  )

  /** Chevron box, and the gap the value keeps from it. */
  const CARET = 14
  let clusterX = 0
  let clusterW = 0
  const indexOf = () => {
    const i = opts.findIndex((o) => o.value === bound.get())
    return i < 0 ? 0 : i
  }
  /** Room for the value, set by layout (it ellipsizes rather than running into the caption). */
  let valRoom = Infinity
  const reflect = () => {
    const label = String(opts[indexOf()]?.label ?? '')
    val.textContent =
      valRoom === Infinity ? label : ellipsize(label, valRoom, TH.TEXT_FONT)
  }
  const step = (d: number) => {
    const n = opts.length
    if (n === 0) return
    let i = indexOf() + d
    i = wrap ? ((i % n) + n) % n : Math.max(0, Math.min(n - 1, i))
    bound.set(opts[i].value)
    reflect()
  }
  const unbind = bound.subscribe(reflect)

  /*
  A SELECT, WITH A CHEVRON THAT SAYS SO.

  It used to draw `‹ value ›` and open a menu when you tapped between the
  arrows. The menu worked; nothing said it was there. A control that looks
  exactly like a stepper IS a stepper as far as a new consumer is concerned —
  ensemble reported the 24-option case as "twenty-three taps" while one tap and
  a list had been available the whole time (#37).

  So: the value carries a downward chevron, the whole cluster opens the list,
  and the arrows are gone. Tonio: "I'd use a downward chevron to indicate you
  can pop up. And, frankly, you could just ditch the cycling behavior."

  Stepping survives in exactly one place — as the fallback when the panel gives
  no host, where a menu is impossible. That is the case the old code called
  out ("a control that is inert in some containers is worse than one that is
  merely plainer"), and it stays true. `panel3d` always provides a host, so in
  a panel this is a select and nothing else.
  */
  let host: WidgetHost | null = null

  /*
  HOLD THE HANDLE, because `closePopup` only knows about the bounded one.

  `host.closePopup()` is the panel's single-popup tracking, which the LAYER path
  knows nothing about — so once a menu opened as a layer, picking an option set
  the value and left the menu standing. Tonio: "Clicking an option on the popup
  in the kitchen sink demo doesn't dismiss the popup."

  Closing the handle you were given works whichever path opened it, which is
  what `showPopup` returning one is for. `closePopup` stays as the fallback for
  the case that produced no handle at all.
  */
  let openHandle: { close: () => void } | null = null

  const closeMenu = (): void => {
    if (openHandle != null) {
      openHandle.close()
      openHandle = null
      return
    }
    host?.closePopup()
  }

  const openMenu = (): void => {
    if (host == null || opts.length === 0) return
    openHandle = host.showPopup(
      {
        // Anchored to the VALUE, not to the whole row: a menu that drops from
        // the far left of a wide row looks unrelated to what it changes.
        anchor: { x: clusterX, y: 0, width: clusterW, height: TH.ROW },
        // Wide enough for its longest option (a list cut off mid-word reads
        // as broken), and never narrower than a comfortable menu.
        width: Math.max(
          clusterW,
          220,
          Math.min(
            420,
            Math.max(
              ...opts.map((o) =>
                measureTextWidth(String(o.label), TH.TEXT_FONT)
              )
            ) +
              TH.PAD_X * 3
          )
        ),
      },
      list3d({
        items: opts.map((o) => ({ label: String(o.label) })),
        handleSelect: (_item, i) => {
          bound.set(opts[i].value)
          reflect()
          if (!config.stayOpen) closeMenu()
        },
      })
    )
  }

  return {
    el,
    dispose: unbind,
    setHost(h) {
      host = h
    },
    layout(width) {
      rowBg.setAttribute('width', String(width))
      clusterW = Math.min(width * 0.55, 180)
      clusterX = width - TH.PAD_X - clusterW
      // Value right-aligned against the chevron, chevron hard against the
      // padding — so the caret sits at a predictable x whatever the value says.
      const caretX = width - TH.PAD_X - CARET
      caret.setAttribute('transform', `translate(${caretX} ${TH.ROW / 2 - 7})`)
      val.setAttribute('x', String(caretX - 8))
      valRoom = Math.max(24, clusterW - CARET - 12)
      // The value cluster wins the right-hand side, so the caption takes what
      // is left of the row rather than printing through it.
      if (lbl) {
        lbl.textContent = ellipsize(
          config.label ?? '',
          clusterX - TH.PAD_X - 8,
          TH.TEXT_FONT
        )
      }
      reflect()
      return TH.ROW
    },
    // The whole cluster is one target; the label area stays a scroll surface.
    hitTest(x) {
      return x >= clusterX - 6
    },
    handle(kind, x) {
      void x
      if (kind === 'leave') {
        rowBg.setAttribute('fill', 'transparent')
        return
      }
      rowBg.setAttribute('fill', TH.ROW_HOVER)
      if (kind === 'up') {
        // One gesture, one meaning: open the list. Stepping remains only where
        // a menu cannot exist — see the note above the chevron.
        if (host != null) openMenu()
        else step(1)
      }
    },
  }
}

/** A vertical list of selectable rows (dialogue options, inventory, …). */
export function list3d<
  T extends { label: string; icon?: string; disabled?: Dynamic<boolean> }
>(config: {
  items: T[]
  /** Fired when a row is chosen. */
  handleSelect?: (item: T, index: number) => void
  rowHeight?: number
}): Widget3d {
  const rowH = config.rowHeight ?? TH.ROW
  const el = css(g({ 'data-w3d': 'list' }), 'cursor:pointer')
  const rowBgs: SVGElement[] = []
  // ASKED EVERY TIME, never cached. `disabled` may be a predicate over live
  // state, so a remembered answer is a stale one — and the two readings that
  // matter (may this row highlight, may it fire) happen at different moments.
  const enabled = (i: number) =>
    resolveDynamic(config.items[i]?.disabled, false) !== true
  // A disabled row never highlights. Highlight is a promise that a press will
  // do something, so lighting a row that cannot fire is a lie the pointer tells
  // before the finger finds out.
  const highlight = (i: number) =>
    rowBgs.forEach((bg, j) =>
      bg.setAttribute('fill', j === i && enabled(j) ? TH.ROW_HOVER : TH.ROW_BG)
    )
  return {
    el,
    layout(width) {
      while (el.firstChild) el.removeChild(el.firstChild)
      rowBgs.length = 0
      // Reserve the icon gutter across the WHOLE list when any row has an icon,
      // so labels line up in a mixed menu rather than stepping in and out.
      const iconSize = Math.round(TH.ROW * 0.5)
      const gutter = config.items.some((it) => it.icon) ? iconSize + 8 : 0
      config.items.forEach((item, i) => {
        const y = i * rowH
        const bg = rect({
          x: 0,
          y: y + 2,
          width,
          height: rowH - 4,
          rx: 6,
          ry: 6,
          fill: TH.ROW_BG,
        })
        const dim = !enabled(i)
        const t = baseText(item.label, dim ? TH.MUTED : TH.TEXT)
        t.setAttribute('x', String(TH.PAD_X + gutter))
        t.setAttribute('y', String(y + rowH / 2))
        rowBgs.push(bg)
        el.appendChild(bg)
        if (item.icon) {
          el.appendChild(
            iconGlyph(item.icon, {
              size: iconSize,
              x: TH.PAD_X,
              y: y + (rowH - iconSize) / 2,
              color: dim ? TH.MUTED : TH.TEXT,
            })
          )
        }
        el.appendChild(t)
      })
      return Math.max(rowH, config.items.length * rowH)
    },
    handle(kind, _x, y) {
      if (kind === 'leave') return highlight(-1)
      const i = Math.floor(y / rowH)
      if (i < 0 || i >= config.items.length) return highlight(-1)
      if (kind === 'up') {
        highlight(-1)
        // Checked at ACTIVATION, not at hover: an item can become disabled
        // during the gesture, and what matters is whether it was allowed at the
        // moment it fired. Same rule as `interaction.ts`'s vetoes.
        if (enabled(i)) {
          handlerOf<(item: T, index: number) => void>(
            config,
            'handleSelect',
            'onSelect'
          )?.(config.items[i], i)
        }
      } else highlight(i) // down / move / hover → highlight the row under it
    },
  }
}

// --- Busy: spinner and progress ---------------------------------------------

/*
ONE ticker for every spinner on the page.

A spinner must animate in BOTH presentations, and that rules out CSS: flat, a
keyframe animation works; rasterised to a texture it does not run AT ALL,
because `SvgTexture` serialises the SVG into its own document where no
stylesheet and no animation clock follow it. A consumer would ship something
that looks right on a desktop and quietly freezes in a headset — exactly the
trap `data-presentation` exists for (tosijs-3d#60, and ensemble spotted it
before we did).

So the rotation is GEOMETRY, updated on a timer. The texture re-rasterises
every 30ms, which is what makes an attribute change show up there at all.

Shared rather than per-spinner so N spinners cost one interval, and stopped
entirely when the last one goes — a page with no spinner has no timer.
*/
const spinners = new Set<{ tick: (t: number) => void }>()
let spinTimer: ReturnType<typeof setInterval> | null = null
let spinPhase = 0

function ensureSpinTicker(): void {
  if (spinTimer != null) return
  spinTimer = setInterval(() => {
    spinPhase += 1
    for (const s of spinners) s.tick(spinPhase)
    if (spinners.size === 0 && spinTimer != null) {
      clearInterval(spinTimer)
      spinTimer = null
    }
  }, 60)
}

export interface Spinner3d extends Widget3d {
  /** Stop the animation and release the shared ticker. */
  dispose: () => void
}

/**
 * An INDETERMINATE busy indicator — "working, duration unknown".
 *
 * The common case, and the one where a fake progress bar lies. Use
 * `progress3d` only when the total is genuinely known.
 *
 * ```javascript
 * const busy = spinner3d({ label: 'loading kits…' })
 * panel.replaceChild(list.el, busy.el)   // when the work finishes
 * busy.dispose()
 * ```
 *
 * **Call `dispose()` when the work ends.** A forgotten spinner does not leak a
 * timer of its own — there is only ever one for the whole page — but it does
 * keep that one alive and keep painting something that is no longer true.
 */
export function spinner3d(
  config: { label?: string; size?: number } = {}
): Spinner3d {
  const size = config.size ?? 18
  const lbl = config.label ? baseText(config.label, TH.MUTED) : null
  /*
  A gapped ring rather than a dot chase: one path, so rotating it is a single
  attribute write per tick, and it reads as motion at any size. A dashed circle
  gives the gap without arc maths.
  */
  const ring = circle({
    r: size / 2 - 2,
    fill: 'none',
    stroke: TH.ACCENT,
    'stroke-width': String(Math.max(2, w3dTheme.strokeWidth)),
    'stroke-linecap': 'round',
    'stroke-dasharray': `${(size - 4) * 1.6} ${(size - 4) * 2}`,
  })
  const el = css(g({ 'data-w3d': 'spinner' }, ring), 'cursor:default')
  if (lbl) el.appendChild(lbl)

  let cx = size / 2
  const cy = TH.ROW / 2
  const entry = {
    tick(t: number) {
      // 30 degrees per tick at 60ms is a touch under two seconds a turn —
      // fast enough to read as busy, slow enough not to strobe on a texture
      // that is only resampled every 30ms.
      ring.setAttribute('transform', `rotate(${(t * 30) % 360} ${cx} ${cy})`)
    },
  }
  spinners.add(entry)
  ensureSpinTicker()

  return {
    el,
    layout(width: number) {
      cx = TH.PAD_X + size / 2
      ring.setAttribute('cx', String(cx))
      ring.setAttribute('cy', String(cy))
      if (lbl) {
        const x = TH.PAD_X + size + 8
        lbl.setAttribute('x', String(x))
        lbl.setAttribute('y', String(cy))
        // `void width` was here: the width was received and deliberately
        // dropped, so a long "loading…" line ran off the panel.
        lbl.textContent = ellipsize(
          config.label ?? '',
          width - x - TH.PAD_X,
          TH.TEXT_FONT
        )
      }
      return TH.ROW
    },
    // Not interactive: a spinner reports, it does not respond. Returning false
    // lets a press fall through to the panel's scroll surface instead of being
    // swallowed by something that will not act on it.
    hitTest() {
      return false
    },
    dispose() {
      spinners.delete(entry)
    },
  }
}

export interface Progress3d extends Widget3d {
  /** Set the fraction, `0..1`. Values outside are clamped. */
  setValue: (fraction: number) => void
}

/**
 * A DETERMINATE progress bar, for when the total is genuinely known — bytes of
 * a GLB, tiles of a terrain, N of M libraries.
 *
 * No timer: it moves when you move it, so nothing animates and nothing needs
 * disposing. If you find yourself faking the fraction, you wanted `spinner3d`.
 */
export function progress3d(
  config: { label?: string; value?: number; showValue?: boolean } = {}
): Progress3d {
  const showValue = config.showValue ?? true
  const lbl = config.label ? baseText(config.label) : null
  const track = rect({
    height: 6,
    rx: 3,
    ry: 3,
    fill: TH.ROW_BG,
  })
  const fill = rect({ height: 6, rx: 3, ry: 3, fill: TH.ACCENT })
  const pct = showValue ? baseText('', TH.MUTED) : null
  const el = css(g({ 'data-w3d': 'progress' }, track, fill), 'cursor:default')
  if (lbl) el.appendChild(lbl)
  if (pct) el.appendChild(pct)

  let value = Math.max(0, Math.min(1, config.value ?? 0))
  let trackX = 0
  let trackW = 1

  const paint = () => {
    fill.setAttribute('x', String(trackX))
    fill.setAttribute('width', String(Math.max(0, trackW * value)))
    if (pct) pct.textContent = `${Math.round(value * 100)}%`
  }

  return {
    el,
    layout(width: number) {
      const pctW = pct ? 40 : 0
      const labelW = lbl ? Math.min(width * 0.45, 140) : 0
      trackX = TH.PAD_X + labelW
      trackW = Math.max(8, width - trackX - TH.PAD_X - pctW)
      const y = TH.ROW / 2 - 3
      for (const n of [track, fill]) {
        n.setAttribute('x', String(trackX))
        n.setAttribute('y', String(y))
      }
      track.setAttribute('width', String(trackW))
      if (lbl) {
        lbl.setAttribute('x', String(TH.PAD_X))
        lbl.setAttribute('y', String(TH.ROW / 2))
        // It already yields width to the bar; now it says so rather than
        // printing across it.
        lbl.textContent = ellipsize(
          config.label ?? '',
          labelW - 8,
          TH.TEXT_FONT
        )
      }
      if (pct) {
        pct.setAttribute('x', String(trackX + trackW + 8))
        pct.setAttribute('y', String(TH.ROW / 2))
      }
      paint()
      return TH.ROW
    },
    hitTest() {
      return false
    },
    setValue(fraction: number) {
      value = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0))
      paint()
    },
  }
}

// --- Action menu -----------------------------------------------------------

/**
 * One row of an action menu.
 *
 * Distinct from `select3d`'s options, which are VALUES the control then keeps
 * and displays. A menu item is a thing that HAPPENS: it fires and the menu
 * closes, leaving nothing behind. Ensemble put it exactly right — a picker
 * showing a lingering "value" for what was a one-shot action reads wrong
 * (tosijs-3d#59).
 */
export interface MenuAction {
  label: string
  /** Icon name, as `svgIcons`/`iconGlyph` know it. Optional, and mixed lists align. */
  icon?: string
  /**
   * Greyed, unhighlighted and unfirable — but still PRESENT. A menu whose items
   * come and go teaches you nothing about where things are; one that greys them
   * shows you the command exists and is unavailable right now.
   *
   * **Give a PREDICATE, not a boolean**, unless it is genuinely constant. It is
   * asked each time it matters, so the menu can be a module-level constant
   * instead of being rebuilt whenever the state it depends on moves:
   *
   * ```javascript
   * const FILE_MENU = [
   *   { label: 'Save', handleSelect: save },
   *   { label: 'Revert', disabled: () => !doc.dirty, handleSelect: revert },
   * ]
   * ```
   */
  disabled?: Dynamic<boolean>
  /** What this item does. `handleSelect` on the menu fires too, if given. */
  handleSelect?: () => void
}

/**
 * The rows of an action menu, as a `Widget3d`.
 *
 * You rarely want this directly — `openMenu3d` puts it in a popup, which is
 * where a menu belongs. It is separate so a menu can also be embedded in a
 * panel (a permanently-visible command list) without a popup at all.
 */
export function menu3d(config: {
  items: MenuAction[]
  /** Fired for any item, after that item's own `handleSelect`. */
  handleSelect?: (item: MenuAction, index: number) => void
  rowHeight?: number
}): Widget3d {
  return list3d({
    items: config.items,
    rowHeight: config.rowHeight,
    handleSelect: (item, i) => {
      // The item's own handler first, then the menu-wide one: the specific
      // before the general, so a menu-level handler can log or close over the
      // top of whatever the item did.
      item.handleSelect?.()
      config.handleSelect?.(item, i)
    },
  })
}

/**
 * Open an action menu as a popup anchored to something, and close it on pick.
 *
 * This is the piece that was missing. `select3d` could open a menu because it
 * holds a `WidgetHost` privately; nothing else could, so an icon in a tool
 * palette had no route to one (tosijs-3d#59). Any widget that gets a host can
 * now open a menu in one call.
 *
 * ```javascript
 * openMenu3d(host, anchorRect, [
 *   { label: 'Load…', icon: 'uploadCloud', handleSelect: load },
 *   { label: 'Revert', icon: 'rotateCcw', disabled: !dirty, handleSelect: revert },
 * ])
 * ```
 *
 * Dismissal is the host's: a press outside closes it, exactly as it does for a
 * select. `width` defaults to the anchor's, widened to fit the longest label,
 * so a menu hanging off a narrow icon is readable rather than spilling out of
 * its box.
 */
/**
 * How wide a menu must be to show its longest label: the same padding and icon
 * gutter `list3d` lays its rows out with. A menu hung off a narrow button used
 * to take the 160 floor, and any label longer than that ran out of its box.
 */
function menuWidth(items: MenuAction[]): number {
  const gutter = items.some((it) => it.icon) ? Math.round(TH.ROW * 0.5) + 8 : 0
  const widest = Math.max(
    0,
    ...items.map((it) => measureTextWidth(it.label, TH.TEXT_FONT))
  )
  // Plus the popup panel's own padding (panel3d's default, both sides): the
  // rows are laid out inside it, so without this they come up 24 short.
  return Math.ceil(widest + gutter + TH.PAD_X * 2) + 24
}

export function openMenu3d(
  host: WidgetHost,
  anchor: { x: number; y: number; width: number; height: number },
  items: MenuAction[],
  opts: {
    side?: PopupSide
    width?: number
    maxHeight?: number
    /** Fired after an item is chosen (the menu has already closed). */
    handleSelect?: (item: MenuAction, index: number) => void
    handleClose?: () => void
  } = {}
): { close: () => void } | null {
  // An empty menu opens nothing rather than an empty box. Returning null says
  // "no menu happened" so a caller can fall back instead of guessing from a
  // popup handle that closes onto nothing.
  if (items.length === 0) return null
  return host.showPopup(
    {
      anchor,
      side: opts.side,
      width: opts.width ?? Math.max(anchor.width, 160, menuWidth(items)),
      maxHeight: opts.maxHeight,
      handleClose: handlerOf<() => void>(opts, 'handleClose', 'onClose'),
    },
    menu3d({
      /*
      The item's own `handleSelect` is STRIPPED and dispatched below instead of
      by `menu3d`, so that every handler runs after the close.

      That ordering is the point: an action that opens another popup — a
      confirm, a nested menu, a file picker — would otherwise be torn down a
      moment later by its own parent closing on top of it. Leaving the handler
      on the item would run it first and reintroduce exactly that.
      */
      items: items.map(({ handleSelect: _drop, ...rest }) => rest),
      handleSelect: (_stripped, i) => {
        host.closePopup()
        items[i].handleSelect?.()
        opts.handleSelect?.(items[i], i)
      },
    })
  )
}

// --- Container -------------------------------------------------------------

/**
 * A scrollable container. Lays out widgets top-to-bottom; if they overflow the
 * height, clips and enables wheel + drag scrolling. Returns the root `<svg>`,
 * usable as a DOM overlay or as the source element for a `b3dSvgPlane`.
 */
/**
 * A panel sized to its CONTENT, for mounting on a plane in the scene.
 *
 * Returns the SVG plus the height it actually resolved to, because a
 * camera-relative plane needs that number to set its aspect — and reading it
 * back off the element is the only way to get it right once the height is
 * `'fit'` rather than something the caller computed.
 *
 * ## Why this exists rather than three call sites
 *
 * All three in-scene panels — the gear/scene panel, the pause modal and the
 * death dialog — independently computed `46 + rows.length * 48`, which assumes
 * a row is about 48px tall. Most are. `lightEditor3d` is ONE row that lays out
 * over 1200px, so a panel holding it was built ~142px tall and you scrolled a
 * postage stamp inside a headset.
 *
 * That was fixed in the scene panel and left standing in the other two, which
 * take CONSUMER-supplied rows and so have exactly the same exposure. The fix
 * could not propagate because the policy was copied, not shared. It is shared
 * now.
 *
 * `maxHeight` is the caller's, because the right cap depends on where the
 * panel goes: an uncapped `'fit'` on a 1200px editor is a plane several metres
 * tall in your face.
 */
export function fitPanel(
  rows: Widget3d[],
  opts: { width?: number; maxHeight?: number; paddingTop?: number } = {}
): { svg: SVGSVGElement; width: number; height: number } {
  const width = opts.width ?? 320
  const svg = panel3d(
    {
      width,
      height: 'fit',
      maxHeight: opts.maxHeight ?? 620,
      paddingTop: opts.paddingTop,
    },
    ...rows
  )
  // The RESOLVED height, not the requested one — `fit` and the cap have both
  // had their say by now.
  const vb = svg.viewBox.baseVal
  return { svg, width, height: vb.height > 0 ? vb.height : width }
}

/** Height of a panel's title bar (move, title, close), in viewBox units. */
const TITLE_BAR = 28

/** The title bar's glyphs and title, where `chromeLayout` put them. */
function titleBarParts(
  bar: ReturnType<typeof chromeLayout>,
  title: string | undefined,
  closable: boolean,
  width: number,
  padding: number
): SVGElement[] {
  const parts: SVGElement[] = []
  const glyph = (name: string, at: { x: number; y: number; size: number }) =>
    parts.push(
      iconGlyph(name, {
        color: TH.MUTED,
        size: at.size,
        x: at.x,
        y: at.y,
      }) as unknown as SVGElement
    )
  if (bar.move != null) glyph('move', bar.move)
  if (closable) glyph('close', bar.close)
  if (title) {
    const left = bar.move != null ? bar.move.x + bar.move.size + 8 : padding
    const right = closable ? bar.closeHitX - 4 : width - padding
    // Centred on the PANEL, not on the gap between the glyphs (the × is drawn
    // larger than the move, so that gap is off-centre), and ellipsized to the
    // room on its narrower side.
    const mid = width / 2
    const room = 2 * Math.max(0, Math.min(mid - left, right - mid))
    const t = baseText('', TH.TEXT, true)
    t.setAttribute('x', String(mid))
    t.setAttribute('y', String(bar.barHeight / 2 + 1))
    t.setAttribute('text-anchor', 'middle')
    t.textContent = ellipsize(title, room, TH.BOLD_FONT)
    parts.push(t as unknown as SVGElement)
  }
  return parts
}

/**
 * A PANEL'S TITLE BAR OVER CONTENT THAT HAS NONE: move, title and ×, the
 * settings panel's, around any SVG (an inventory dock, a card, a map).
 * Tonio: "why doesn't the inventory floater just get the move and close
 * functionality of a panel by default (possibly with a caption) instead of
 * having the bespoke minus in a circle?"
 *
 * The content is nested under the bar and keeps its own size (followed when
 * it changes); presses below the bar reach its `handlePointer` in its own
 * coordinates, and one that began there stays its own until release (a drag
 * out of a bag). `gripHit` is the bar less the close target, as `panel3d`'s
 * is, so a host drags it the same way. `scrollBy`, `scrollTop` and
 * `scrollable` are the content's (a wheel over the panel scrolls it).
 * `setContent(next)` puts other content under the same bar (the old content
 * goes back where it came from); the frame, and whatever plane shows it, stay.
 * `dispose` gives the content back.
 */
export function titled3d(
  first: SVGSVGElement,
  config: { title?: string; grip?: boolean; handleClose?: () => void } = {}
): SVGSVGElement & {
  dispose: () => void
  setContent: (next: SVGSVGElement) => void
} {
  const handleClose = handlerOf<() => void>(config, 'handleClose', 'onClose')
  const grip = config.grip !== false
  const radius = w3dTheme.roundedRadius * 2
  const root = css(
    svg({ 'data-w3d': 'titled' }),
    'pointer-events:auto;user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent'
  ) as unknown as SVGSVGElement & {
    dispose: () => void
    setContent: (next: SVGSVGElement) => void
  }
  const bg = rect({ x: 0, y: 0, rx: radius, ry: radius, fill: TH.PANEL_BG })
  const chrome = g({})
  let content = first
  // Where the content lived, to give it back there (a panel's own child).
  let home = content.parentNode
  let next = content.nextSibling
  /*
  ONE OUTLINE. A panel3d wears its own rim; framed, it sat just inside the
  frame's, a second border under the title bar (Tonio: "like there's a second
  frame in it"). The content's rim is hidden while it is framed, and shown
  again when it is given back.
  */
  const ownRims = (c: SVGSVGElement) =>
    Array.from(c.children).filter((n) =>
      n.hasAttribute('data-w3d-rim')
    ) as SVGElement[]
  const quiet = (c: SVGSVGElement) => {
    for (const r of ownRims(c)) r.style.display = 'none'
  }
  quiet(content)
  root.append(bg, content, chrome)
  let bar = chromeLayout(1, 1, 1, grip)
  let w = 0
  let h = 0
  const relayout = () => {
    const vb = (content.getAttribute('viewBox') ?? '').split(/[\s,]+/)
    const cw = Number(vb[2]) || Number(content.getAttribute('width')) || 300
    const ch = Number(vb[3]) || Number(content.getAttribute('height')) || 200
    const H = ch + TITLE_BAR
    if (cw === w && H === h) return
    w = cw
    h = H
    content.setAttribute('x', '0')
    content.setAttribute('y', String(TITLE_BAR))
    root.setAttribute('viewBox', `0 0 ${w} ${h}`)
    root.setAttribute('width', String(w))
    root.setAttribute('height', String(h))
    bg.setAttribute('width', String(w))
    bg.setAttribute('height', String(h))
    bar = chromeLayout(w, h, TITLE_BAR / h, grip)
    chrome.replaceChildren(
      ...titleBarParts(bar, config.title, handleClose != null, w, TH.PAD_X),
      panelRim(w, h, radius)
    )
  }
  relayout()
  const watch =
    typeof MutationObserver === 'function'
      ? new MutationObserver(relayout)
      : null
  // Any attribute of the root (it has few): relayout returns early unless the
  // size changed. (No attributeFilter: happy-dom matched no 'viewBox' with one.)
  watch?.observe(content, { attributes: true })
  const giveBack = () => {
    for (const r of ownRims(content)) r.style.display = ''
    content.removeAttribute('x')
    content.removeAttribute('y')
    if (home != null)
      home.insertBefore(content, next?.parentNode === home ? next : null)
    else content.remove()
  }
  type Target = {
    handlePointer?: (k: PointerKind, x: number, y: number) => void
  }
  const forward = (kind: PointerKind, x: number, y: number) =>
    (content as unknown as Target).handlePointer?.(kind, x, y - TITLE_BAR)
  let inContent = false
  let closing = false
  const handlePointer = (kind: PointerKind, x: number, y: number) => {
    const onBar = y <= bar.barHeight
    const onClose = onBar && handleClose != null && x >= bar.closeHitX
    if (closing) {
      if (kind === 'up' || kind === 'leave') {
        closing = false
        if (kind === 'up' && onClose) handleClose!()
      }
      return
    }
    if (kind === 'down') {
      if (onClose) {
        closing = true
        return
      }
      inContent = !onBar
      if (onBar) return
    }
    if (kind === 'leave' || (onBar && !inContent)) {
      forward('leave', 0, TITLE_BAR)
      if (kind !== 'move') inContent = false
      return
    }
    forward(kind, x, y)
    if (kind === 'up') inContent = false
  }
  // Scrolling is the content's, in the same units (a host's wheel, #114).
  // Read through `content` each time: `setContent` replaces it.
  const scrolling = () =>
    content as unknown as {
      scrollBy?: (dy: number) => void
      scrollTop?: number
      scrollable?: boolean
    }
  Object.defineProperties(root, {
    scrollTop: {
      get: () => scrolling().scrollTop ?? 0,
      set: (v: number) => {
        if ('scrollTop' in scrolling()) scrolling().scrollTop = v
      },
    },
    scrollable: { get: () => !!scrolling().scrollable },
  })
  Object.assign(root, {
    handlePointer,
    scrollBy: (dy: number) => scrolling().scrollBy?.(dy),
    gripHit: (x: number, y: number) =>
      grip &&
      y <= bar.barHeight &&
      !(handleClose != null && x >= bar.closeHitX),
    /*
    IN PLACE, so nothing between the two contents can miss a press. Swapping
    by closing and reopening the panel left a moment with no plane, and a
    click that landed in it was lost (ensemble, #111: one run in four after
    a store refresh swapped every panel's content).
    */
    setContent: (c: SVGSVGElement) => {
      if (c === content) return
      // A gesture in the old content ends there; the new one starts clean.
      if (inContent) forward('leave', 0, TITLE_BAR)
      inContent = false
      watch?.disconnect()
      giveBack()
      content = c
      home = c.parentNode
      next = c.nextSibling
      quiet(c)
      root.insertBefore(c, chrome)
      w = 0
      h = 0
      relayout()
      watch?.observe(content, { attributes: true })
    },
    dispose: () => {
      watch?.disconnect()
      giveBack()
    },
  })
  return root
}

/**
 * What `panel3d` returns: its SVG, with what a host drives it by. Typed in
 * full so a consumer need not cast (tosijs-3d#104: `dispose` was missing from
 * the declaration, so code that called it did not typecheck).
 */
export type Panel3d = SVGSVGElement & {
  /** Feed a press, move or release in viewBox units (no DOM events). */
  handlePointer: (kind: PointerKind, x: number, y: number) => void
  /** Scroll by a delta in viewBox units; nothing when it does not overflow. */
  scrollBy: (dy: number) => void
  /**
   * How far the body is scrolled, in viewBox units (clamped on set). Read it
   * before rebuilding a panel and set it on the new one to keep the place.
   */
  scrollTop: number
  /**
   * Scroll so `widget` (one of this panel's rows) is in view; `within` is a
   * band inside it (`table.rowBox(id)` for one row). Returns false when the
   * widget is not in the scrolling body. Works in a headset, where a host has
   * no DOM to measure.
   */
  reveal: (
    widget: Widget3d,
    within?: { top: number; height: number }
  ) => boolean
  /** Does the content overflow? */
  scrollable: boolean
  /**
   * Is a press in progress (a control captured, or a scroll-drag)? A host
   * that rebuilds the panel waits for the release, so a slider drag is not
   * cut off by an unrelated refresh.
   */
  readonly pressing: boolean
  /** Height of the title-bar band, in viewBox units. */
  gripHeight: number
  /** Is this point on the bar a host may drag by (the bar less the close)? */
  gripHit: (x: number, y: number) => boolean
  /** The panel's size and what its content needs. */
  measure: () => PanelFit
  /** Where popups go when the panel cannot hold them; returns the removal. */
  addLayerHost: (fn: LayerHost) => () => void
  /** Popups as DOM in `container` (flat only); returns the removal. */
  useDomLayer: (container?: HTMLElement) => () => void
  /** Dispose its rows: unsubscribes bound widgets (#100). */
  dispose: () => void
}

export function panel3d(
  config: {
    width?: number
    /**
     * Fixed height, or `'fit'` to size to the content (the default).
     *
     * `'fit'` exists because clipping is SILENT — a panel too short for its
     * content looks exactly like a panel missing its last control, so a
     * hand-tuned constant is wrong the moment the content changes. See
     * `panelHeight`.
     */
    height?: number | 'fit'
    /** Upper bound for `height: 'fit'`. Past it the panel scrolls instead of growing. */
    maxHeight?: number
    padding?: number
    /** Top padding, if it should differ from `padding` (e.g. to clear a close button). */
    paddingTop?: number
    gap?: number
    background?: string
    /**
     * Rows PINNED to the top — laid out above the scrolling body and never
     * moved by it.
     *
     * A panel's own chrome (an icon bar with Exit VR on it) must not scroll
     * away, because the control you need in order to leave is the one you
     * cannot reach once it has gone. The flat presentation has always pinned
     * its bar — it renders those buttons as DOM header elements outside this
     * SVG — so without this the two presentations disagree, which is the
     * divergence UI-DESIGN-NOTES warns about.
     */
    header?: Widget3d[]
    /**
     * Draw a grab bar in the top padding strip and report `gripHeight`.
     *
     * The panel does not act on it — the HOST does, because what a drag means
     * depends on where the panel lives (in a headset `tosi-b3d` orbits it
     * around the rig anchor; a DOM overlay would move it in the page).
     */
    grip?: boolean
    /**
     * A title, drawn in the top band beside the move glyph. A panel with a
     * name says it here rather than spending a row of its body on it.
     */
    title?: string
    /**
     * Draw a close glyph at the band's right, and call this on a press there.
     * The panel handles that press itself, so every host gets it.
     */
    handleClose?: () => void
  },
  ...widgets: Widget3d[]
): Panel3d {
  const width = config.width ?? 360
  const padding = config.padding ?? 12
  const handleClose = handlerOf<() => void>(config, 'handleClose', 'onClose')
  // A title bar (move, title, close) needs a band to sit in.
  const hasBar = !!(config.grip || config.title || handleClose)
  const paddingTop = config.paddingTop ?? (hasBar ? TITLE_BAR : padding)
  // Half the theme spacing between stacked rows: each row pads itself.
  const gap = config.gap ?? TH.GAP / 2
  const innerW = width - padding * 2

  /*
  LAY OUT BEFORE CHOOSING THE HEIGHT.

  Widgets measure themselves against the inner WIDTH, which is known from
  `width` alone — so the whole stack can be measured before the panel has a
  height, and the height can then be derived from it. That ordering is the
  whole trick behind `height: 'fit'`; the previous code fixed the height first
  and had no way to find out it was wrong.
  */
  const headerWidgets = config.header ?? []
  const headerHeights = headerWidgets.map((w) => w.layout(innerW))
  const headerLayout = stackLayout(headerHeights, gap)
  // The pinned block costs the body its height, plus half a gap to separate
  // them: the body's first row has its own padding above its content.
  const headerH = headerWidgets.length > 0 ? headerLayout.total + gap / 2 : 0

  /*
  A SCROLL RAIL, because a packed panel has nowhere to start a scroll.

  Scrolling begins on EMPTY SPACE — and a panel full of sliders has none, so in
  a headset there was no gesture that scrolled it at all. Every attempt landed
  on a control instead. Tonio: "I think we need to add an affordance to the side
  of scrolling panels that directly scrolls the panel. When I tried to scroll
  this thing I somehow zeroed every slider with one drag."

  It is also the only visible sign that there IS more below, which a texture on
  a plane badly needs — there is no scrollbar, no overflow shadow and no
  momentum to tell you.

  Measured twice on purpose: whether it scrolls is only known once the rows are
  laid out, and the rail costs the rows width. So measure full-width, and if it
  scrolls, measure again with the rail's width taken out. Cheap (layout is
  arithmetic) and it keeps the rail from ever overlapping a control.
  */
  /*
  What the BODY gives up for the rail. The thumb is drawn flush with the panel's
  own right inset — the same one the top and bottom use — so the only reserved
  space is the thumb plus a small gap, rather than a wide column with dead space
  inside it. Tonio: "move it a bit closer to the edge of the panel (same right
  inset as bottom/top inset). Regain a little real estate and look a little
  nicer, and it's no harder to point at with the virtual laser pointer."

  It is in fact EASIER to point at: the hit zone is everything from here to the
  panel's edge, so it claims the padding beyond the thumb as well — about twice
  the drawn width, and none of it overlapping a control.
  */
  const RAIL_W = 10
  const measure = (w: number) => {
    const hs = widgets.map((x) => x.layout(w))
    return { hs, ...stackLayout(hs, gap) }
  }
  let bodyW = innerW
  let m = measure(bodyW)
  const provisionalViewport =
    panelHeight(
      m.total + headerH,
      paddingTop,
      padding,
      config.height ?? 'fit',
      config.maxHeight
    ) -
    paddingTop -
    padding -
    headerH
  const needsRail = m.total > provisionalViewport
  if (needsRail) {
    bodyW = innerW - RAIL_W
    m = measure(bodyW)
  }
  const heights = m.hs
  const { offsets, total } = m
  const height = panelHeight(
    total + headerH,
    paddingTop,
    padding,
    config.height ?? 'fit',
    config.maxHeight
  )
  // The SCROLLING viewport excludes the pinned block — otherwise the body is
  // laid out against a taller area than it actually gets, and the last row is
  // unreachable by exactly `headerH`.
  const viewport = height - paddingTop - padding - headerH
  // The title bar's glyphs, laid out as a popup's are (one vocabulary).
  const bar = hasBar
    ? chromeLayout(width, height, paddingTop / height, !!config.grip)
    : null

  // Defend against a host page's global `svg { pointer-events: none }` (it's
  // inherited, so re-enabling the root re-enables the whole subtree).
  // user-select/tap-highlight off kills the blue selection flash on click+drag.
  const root = css(
    svg({
      viewBox: `0 0 ${width} ${height}`,
      width,
      height,
      'data-w3d': 'panel',
    }),
    'pointer-events:auto;user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent'
  ) as unknown as SVGSVGElement
  const bg = rect({
    x: 0,
    y: 0,
    width,
    height,
    rx: w3dTheme.roundedRadius * 2,
    ry: w3dTheme.roundedRadius * 2,
    fill: config.background ?? TH.PANEL_BG,
  })

  // The panel's one tooltip, over its body and outside the clip (#112).
  const tips = tipDrawer()
  const clipId = `w3d-clip-${clipSeq++}`
  const clip = clipPath(
    { id: clipId },
    rect({
      x: padding,
      y: paddingTop + headerH,
      width: innerW,
      height: viewport,
    })
  )
  // The clip lives on a NON-transformed wrapper, so its rect is in viewBox space —
  // unambiguous in both the live DOM and the SVG→texture rasterizer. Clipping the
  // TRANSLATED `content` group directly makes the two renderers disagree: the VR
  // texture path double-offsets the clip and crops the top-left of the list.
  const clipWrap = g()
  const content = g({ transform: `translate(${padding}, ${paddingTop})` })
  /*
  ⚠️ THE PINNED HEADER LIVES OUTSIDE THE CLIP, and putting it inside cost the
  headset its Exit VR button.

  The clip rect starts at `paddingTop + headerH` — it has to, or the body is
  laid out against a taller area than it gets. The header sits ABOVE that, at
  y 0..headerH, so anything clipped by that rect is clipped away entirely.

  It was appended to `content`, which is correct for keeping it out of the
  SCROLL transform and wrong for keeping it out of the CLIP. The result was a
  header that reserved its space (a band of empty panel), routed its presses
  correctly (the router computes header coords independently), and painted
  NOTHING — so the buttons were hittable if you guessed where they were, and
  invisible. Tonio, from the headset: "the standard buttons have now
  disappeared… the panel has a LOT of black headroom… I can click where I think
  they should be and stuff happens."

  And it only bites when the panel SCROLLS, because the clip is only applied
  then — so every small panel in the demos and tests was fine and the terrain
  editor (1104px of rows in a 508px viewport) was not.
  */
  const headerGroup = g({ transform: `translate(${padding}, ${paddingTop})` })
  // The scrolling body sits BELOW the pinned block. `scrollGroup` is what the
  // scroll transform moves, so the offset lives on its parent — otherwise
  // scrolling would drag the body up over the header.
  const bodyGroup = g({ transform: `translate(0, ${headerH})` })
  const scrollGroup = g()
  bodyGroup.appendChild(scrollGroup)
  content.appendChild(bodyGroup)
  clipWrap.appendChild(content)

  const headerRows = headerWidgets.map((w, i) => ({
    w,
    top: headerLayout.offsets[i],
    height: headerHeights[i],
  }))
  headerWidgets.forEach((w, i) => {
    w.el.setAttribute('transform', `translate(0, ${headerLayout.offsets[i]})`)
    // `headerGroup`, which is neither scrolled NOR clipped. Both halves matter:
    // `scrollGroup` would move it, `content` would clip it.
    headerGroup.appendChild(w.el)
  })

  const rows = widgets.map((w, i) => ({
    w,
    top: offsets[i],
    height: heights[i],
  }))
  widgets.forEach((w, i) => {
    w.el.setAttribute('transform', `translate(0, ${offsets[i]})`)
    scrollGroup.appendChild(w.el)
  })

  const scrollable = total > viewport
  if (scrollable) clipWrap.setAttribute('clip-path', `url(#${clipId})`)

  // The rail: a track down the right inside edge, and a thumb sized by how much
  // of the content you can see. Drawn only when there is something to scroll —
  // a rail on a panel that fits is chrome that means nothing.
  const railX = width - padding - RAIL_W
  const railTop = paddingTop + headerH
  const THUMB_W = 6
  // Flush with the right inset, not centred in a column of its own.
  const thumbX = width - padding - THUMB_W
  const thumbH = Math.max(
    24,
    Math.round(viewport * Math.min(1, viewport / Math.max(1, total)))
  )
  const railTrack = rect({
    x: thumbX,
    y: railTop,
    width: THUMB_W,
    height: viewport,
    rx: THUMB_W / 2,
    fill: TH.TRACK,
  })
  const railThumb = rect({
    x: thumbX,
    y: railTop,
    width: THUMB_W,
    height: thumbH,
    rx: THUMB_W / 2,
    fill: TH.MUTED,
  })

  let scroll = 0
  let tipScroll = 0
  const applyScroll = () => {
    // A tip belongs where the control WAS; hide it only on a real scroll
    // (this also runs on every layout).
    if (scroll !== tipScroll) {
      tipScroll = scroll
      tips.hide()
    }
    scroll = clampScroll(scroll, total, viewport)
    scrollGroup.setAttribute('transform', `translate(0, ${-scroll})`)
    if (scrollable) {
      const span = Math.max(1, total - viewport)
      const travel = viewport - thumbH
      railThumb.setAttribute('y', String(railTop + (scroll / span) * travel))
    }
  }

  // The single pointer authority, in the panel's viewBox coords. Hit-tests by
  // layout + scroll, captures the pressed widget for the whole press, and
  // scroll-drags from empty area. Coordinate-based, so the overlay and the
  // in-scene/VR host feed it the same way.
  // `fromHeader` because a capture must keep the coordinate space it STARTED
  // in: drag off a pinned control into the body and `inHeader` flips, which
  // would hand the captured widget coords from the other space mid-gesture.
  let captured: { w: Widget3d; top: number; fromHeader: boolean } | null = null
  /** The widget that last took a press — see the `focusClear` note below. */
  let lastPressed: Widget3d | null = null
  let hovered: { w: Widget3d; top: number } | null = null
  let scrollFrom = 0
  let scrolling = false
  // A row counts as "hit" only where its widget's control is — outside that
  // (a switch/slider's dead row space) the press falls through to scroll-drag.
  const rowAt = (localX: number, contentY: number) =>
    localX >= 0 && localX <= innerW
      ? rows.find(
          (r) =>
            r.w.handle != null &&
            contentY >= r.top &&
            contentY < r.top + r.height &&
            (r.w.hitTest == null || r.w.hitTest(localX, contentY - r.top))
        )
      : undefined
  const headerRowAt = (localX: number, localY: number) =>
    localX >= 0 && localX <= innerW
      ? headerRows.find(
          (r) =>
            r.w.handle != null &&
            localY >= r.top &&
            localY < r.top + r.height &&
            (r.w.hitTest == null || r.w.hitTest(localX, localY - r.top))
        )
      : undefined
  const setHover = (next?: { w: Widget3d; top: number }) => {
    if ((next && next.w) !== (hovered && hovered.w)) {
      if (hovered) hovered.w.handle?.('leave', 0, 0)
      hovered = next ? { w: next.w, top: next.top } : null
    }
  }
  /*
  THE OVERLAY: at most one popup, mounted above the content.

  One, not a stack — a dropdown opening a dropdown is a cascade, and a cascade
  wants `surface.ts`, which already does it properly. Pretending to support one
  here would be a worse cascade than no cascade.
  */
  let overlay: {
    el: SVGSVGElement & {
      handlePointer?: (k: PointerKind, x: number, y: number) => void
    }
    x: number
    y: number
    w: number
    h: number
    onClose?: () => void
  } | null = null

  const closeOverlay = (): void => {
    if (overlay == null) return
    overlay.el.remove()
    const notify = overlay.onClose
    overlay = null
    /*
    TELL THE OPENER. An outside press dismisses the overlay directly, so without
    this the opener still believes its popup is up — and a field that thinks its
    keyboard is open refuses to reopen it, which presents as "focusing the field
    does nothing".
    */
    notify?.()
  }

  /*
  A host PER WIDGET, so the anchor can be given in the widget's own coordinates.

  A widget knows where things are inside itself and nothing about where it sits
  in the panel, so an anchor in panel coordinates is a number it cannot produce.
  The first version made select3d pass `y: 0` and the menu opened at the top of
  the panel, over an unrelated control — right list, wrong place.

  The panel is the only thing that knows both, so it translates: widget-local in,
  panel coordinates out, including the current scroll. Each widget gets a host
  closed over its own index rather than sharing one, which is also what lets
  `showPopup` need no "which widget is calling" argument.
  */
  /*
  WHERE A ROW IS DRAWN, in the content frame (below the top padding). Body rows
  sit under the pinned block and move with the scroll; header rows sit at their
  own offsets and do not. This used to be `offsets[index] - scroll` for both —
  wrong by `headerH` for every body row once a header existed, and header
  widgets were never handed a host at all.
  */
  const rowY = (index: number, inHeader: boolean): number =>
    inHeader
      ? headerLayout.offsets[index] ?? 0
      : headerH + (offsets[index] ?? 0) - scroll
  const hostFor = (index: number, inHeader = false): WidgetHost => ({
    /*
    A POPUP IS A POPUP — it prefers a LAYER and only falls back to being bounded
    by the panel when there genuinely is not one.

    This used to go straight to the bounded path, so every `select3d` menu and
    every `openMenu3d` was cropped to the room left under its own row. Only the
    keyboard called `showLayer`, and only because someone knew it existed —
    which is the failure `showLayer`'s own comment already names: "a feature that
    only works if you know a second call exists is a feature most people do not
    have."

    Measured on the second `tosi-b3d` demo, whose panel holds a `spin` select:
    zero DOM layers installed and the menu mounted INSIDE the panel's own SVG.
    Tonio, on both the flat and the VR presentation: "the pop up for speed is
    tiny… and it's clipped to the panel. We need pop ups to be pop ups and not
    constrained by the thing that pops them. Otherwise the user experience is
    terrible."

    `showLayer` already degrades to `boundedPopup` when there is nowhere to
    mount, so this is one door with the right default behind it rather than two
    doors a caller has to choose between. `boundedPopup` stays reachable for the
    one case that wants it: a popup that must not escape its panel.
    */
    showPopup(config, ...items) {
      /*
      ONE ANCHORED POPUP PER PANEL. A menu belongs to the control that opened
      it, and opening another control's menu means you are done with the
      first. The bounded path below has always closed its predecessor; this
      one, which is the path every menu actually takes, did not, so tapping
      one select and then another left both lists up, one over the other.
      (Seen in a headset, reproduced flat in a minute.)

      Kept on the root, not in a closure variable: this object is built per
      widget and the rule is per PANEL.
      */
      const slot = root as unknown as { __anchored?: { close: () => void } }
      slot.__anchored?.close()
      const handle = this.showLayer!(config, ...items)
      slot.__anchored = handle
      return handle
    },
    boundedPopup(config, ...items) {
      return baseHost.showPopup(
        {
          ...config,
          anchor: {
            ...config.anchor,
            x: config.anchor.x + padding,
            y: config.anchor.y + paddingTop + rowY(index, inHeader),
          },
        },
        ...items
      )
    },
    closePopup: () => {
      const slot = root as unknown as { __anchored?: { close: () => void } }
      slot.__anchored?.close()
      slot.__anchored = undefined
      baseHost.closePopup()
    },
    relayout: () => baseHost.relayout(),
    showTip(t, anchor) {
      root.appendChild(tips.el) // over anything appended since
      tips.show(
        t,
        {
          ...anchor,
          x: anchor.x + padding,
          y: anchor.y + paddingTop + rowY(index, inHeader),
        },
        width,
        height
      )
    },
    hideTip: () => tips.hide(),
    showLayer(config, ...items) {
      /*
      FAN OUT TO EVERY PRESENTATION.

      A panel is usually on screen twice — flat in the DOM and rasterised onto a
      plane — and a layer belongs to a PRESENTATION, not to the panel. The first
      version took a single host, `panelScene` installed it, and the keyboard
      moved onto a plane and disappeared from the DOM: measured as 0 popups flat,
      2 planes in the scene.

      So each presentation adds its own mounter and a popup opens in all of them,
      which is the same arrangement that makes the panel itself work in both
      places. Closing closes them together, because they are one popup wearing
      two faces — exactly as the panel is.
      */
      let hosts =
        (root as unknown as { __layerHosts?: LayerHost[] }).__layerHosts ?? []

      /*
      AUTO-INSTALL THE DOM LAYER when the panel is on the page and nobody has
      volunteered one.

      `useDomLayer` was opt-in, and that was wrong: a consumer rendering a panel
      flat has no reason to know the method exists, so they got the degraded path
      — a popup mounted INSIDE the panel, cropped by its viewBox, and refused
      outright on a short panel. Reported from ensemble, which is DOM-only:
      measured 0 popups on a `height: 'fit'` panel and a clipped one on a tall
      one.

      A feature that only works if you know a second call exists is a feature
      most people do not have. `useDomLayer(container)` remains, for choosing a
      DIFFERENT container than the panel's own parent.
      */
      // `parentNode`, not `parentElement`: a panel inside a ShadowRoot has no
      // parent ELEMENT, and that is exactly the case that rendered nothing.
      if (hosts.length === 0 && root.isConnected && root.parentNode != null) {
        ;(
          root as unknown as { useDomLayer: (c?: HTMLElement) => () => void }
        ).useDomLayer()
        hosts =
          (root as unknown as { __layerHosts?: LayerHost[] }).__layerHosts ?? []
      }

      if (hosts.length === 0) {
        // Genuinely nowhere to put it — a detached panel, or one whose parent
        // has gone. Degrades to a popup bounded by the panel.
        //
        // `boundedPopup`, NOT `showPopup`: that now prefers a layer and would
        // call straight back into here.
        return hostFor(index, inHeader).boundedPopup!(config, ...items)
      }
      const placed = {
        ...config,
        anchor: {
          ...config.anchor,
          x: config.anchor.x + padding,
          y: config.anchor.y + paddingTop + rowY(index, inHeader),
        },
      }
      /*
      ONE sheet, mounted by each presentation — see `LayerHost`. The chrome band
      is reserved only if one of THESE hosts actually draws chrome, so a
      flat-only panel gets no empty strip above its menu.
      */
      /*
      CHROME IS A PROPERTY OF THE POPUP, not of the host.

      It used to be "does any mounting host draw glyphs", which is right for the
      in-scene face — `popup-surface` always draws them, so the band must exist
      or they land on the content — and wrong for a MENU, which wants neither a
      title bar nor a way to be dragged. Reserving it regardless is what put an
      empty strip above every flat dropdown.

      So: a host that always draws (the plane) still forces the band, and
      otherwise the CALLER says. `panel.popup` asks for chrome; a select does
      not.
      */
      const alwaysDraws = hosts.some(
        (h) => (h as unknown as { drawsChrome?: boolean }).drawsChrome === true
      )
      const chromeBand =
        alwaysDraws || config.chrome === true ? POPUP_CHROME_BAND : 0
      /*
      NO TALLER THAN THE ROOM A HOST HAS. A host bounded by something (a
      panel pinned to the screen) says how tall a popup can be there
      (`roomFor`); the shared sheet takes the smallest, and scrolls past it.
      Tonio, of a 27-planet picker hanging off the bottom of the scene: "it
      doesn't fit".
      */
      const room = hosts
        .map((h) =>
          (
            h as unknown as { roomFor?: (y: number) => number | undefined }
          ).roomFor?.(placed.anchor.y)
        )
        .filter((n): n is number => n != null && n > 0)
      const sheet = panelPopupSheet(
        config.width ?? 360,
        items,
        chromeBand,
        Math.min(config.maxHeight ?? POPUP_MAX_HEIGHT, ...room)
      )
      // The caption goes IN the sheet, which every presentation shares, so
      // it reads the same flat and on a plane. Between the two glyphs.
      const title = String((config as { title?: string }).title ?? '')
      if (chromeBand > 0 && title !== '') {
        const cap = baseText(title)
        cap.setAttribute('x', String(chromeBand + 2))
        cap.setAttribute('y', String(chromeBand / 2 + 1))
        cap.setAttribute('fill', TH.MUTED)
        cap.setAttribute('font-size', String(Math.round(TH.FONT * 0.85)))
        cap.setAttribute('pointer-events', 'none')
        sheet.appendChild(cap)
      }
      /*
      ONE CLOSE, however it starts.

      Every face is told `handleClosed`, so pressing × on the in-scene one shuts
      the flat one too and the opener hears about it — the popup is one thing
      wearing two faces, and a close that only reached the face you pressed left
      the other up and the opener out of step.

      `closing` guards the loop that creates: a host's own close calls back in
      here, which closes that host again.
      */
      let closing = false
      const opened: Array<{ close: () => void }> = []
      const closeAll = (): void => {
        if (closing) return
        closing = true
        for (const o of opened) o.close()
        handlerOf<() => void>(config, 'handleClose', 'onClose')?.()
      }
      opened.push(
        // `chromeBand` travels with the popup: the sheet reserved it, so every
        // host has to agree about whether it exists. Computing it twice is how
        // glyphs end up drawn over content in one presentation and floating in
        // an empty strip in the other.
        ...hosts.map((h) =>
          h(sheet, { ...placed, handleClosed: closeAll, chromeBand })
        )
      )
      return { close: closeAll }
    },
    get hasLayer() {
      const hosts =
        (root as unknown as { __layerHosts?: LayerHost[] }).__layerHosts ?? []
      if (hosts.length > 0) return true
      /*
      A DOM layer will be installed ON DEMAND (see `showLayer`), so a panel on
      the page effectively has one already.

      Reporting `false` here created a chicken-and-egg: the keyboard consults
      this to decide whether the room check applies, that check refused on a
      short panel, and `showLayer` was therefore never called — so the layer that
      would have made the check unnecessary was never installed. Measured as a
      `height: 'fit'` panel producing no keyboard at all.
      */
      return root.isConnected && root.parentNode != null
    },
    // Live getters: both change when the panel re-lays-out or scrolls, and a
    // widget that cached them would decide against stale geometry.
    get bounds() {
      return { width, height }
    },
    get top() {
      return paddingTop + rowY(index, inHeader)
    },
  })

  // `showLayer` is per-widget (it needs the widget's offset), so the shared base
  // deliberately does not carry it.
  const baseHost: Omit<
    WidgetHost,
    'bounds' | 'top' | 'showLayer' | 'hasLayer'
  > = {
    showPopup(config, ...items) {
      // One at a time: opening a second while the first is up would leave the
      // first unreachable but still drawn, which reads as a stuck panel.
      closeOverlay()
      const opened = (
        root as unknown as {
          openPopup: (
            c: typeof config,
            ...i: Widget3d[]
          ) => {
            el: SVGSVGElement
            x: number
            y: number
            side: PopupSide
            close: () => void
          }
        }
      ).openPopup(config, ...items)
      const el = opened.el as SVGSVGElement & {
        handlePointer?: (k: PointerKind, x: number, y: number) => void
      }
      el.setAttribute('x', String(opened.x))
      el.setAttribute('y', String(opened.y))
      // Appended to ROOT, after the clipped content — so it is not clipped by
      // the content's clip path, but is still inside the panel's viewBox.
      root.appendChild(el)
      overlay = {
        el,
        x: opened.x,
        y: opened.y,
        w: Number(el.getAttribute('width')),
        h: Number(el.getAttribute('height')),
        onClose: handlerOf<() => void>(config, 'handleClose', 'onClose'),
      }
      return { close: closeOverlay }
    },
    closePopup: closeOverlay,
    relayout: () => {
      // Re-running the panel's own layout is not exposed; the cheap correct
      // thing is to let the caller redraw, and widgets that change height are
      // rare enough that this is honest rather than lazy.
      closeOverlay()
    },
  }

  /*
  Handed out HERE, after `host` exists — not up beside the layout loop, which
  runs earlier and would read it in its temporal dead zone.

  That is the second time today: `popup-surface` had the identical shape this
  morning (`attachDrag` used above its own `const`). Same lesson, and this time a
  test caught it rather than a person, which is the difference between a bug and
  a five-minute detour.
  */
  widgets.forEach((w, i) => w.setHost?.(hostFor(i)))
  headerWidgets.forEach((w, i) => w.setHost?.(hostFor(i, true)))

  // Whether a press began on the close glyph: it closes on RELEASE there,
  // as a button does, so a press that slides off is not a close.
  let closing = false
  const handlePointer = (kind: PointerKind, x: number, y: number) => {
    if (kind === 'leave' || kind === 'down') tips.hide()
    if (handleClose != null && bar != null) {
      const onClose = y <= bar.barHeight && x >= bar.closeHitX
      if (kind === 'down' && onClose && overlay == null) {
        closing = true
        return
      }
      if (closing) {
        if (kind === 'up' || kind === 'leave') {
          closing = false
          if (kind === 'up' && onClose) handleClose()
        }
        return
      }
    }
    /*
    THE OVERLAY WINS, and an outside press dismisses.

    Checked before anything else, because the popup is drawn on top: routing by
    what is underneath would let you press a control through an open menu, which
    is the "it clicked the wrong thing" bug in its purest form.
    */
    if (overlay != null) {
      const inside =
        x >= overlay.x &&
        x <= overlay.x + overlay.w &&
        y >= overlay.y &&
        y <= overlay.y + overlay.h
      if (inside) {
        overlay.el.handlePointer?.(kind, x - overlay.x, y - overlay.y)
        return
      }
      // Dismiss on PRESS, not on release: a press that starts outside was never
      // meant for the menu, and waiting for the release leaves it open under a
      // pointer that has already moved on.
      if (kind === 'down') {
        closeOverlay()
        return
      }
      if (kind !== 'move') return
    }
    const localX = x - padding
    if (kind === 'leave') return setHover(undefined)
    /*
    TWO COORDINATE SPACES, and only one of them scrolls.

    A pinned header row sits at a fixed y; a body row moves under the scroll.
    Routing both through one `contentY` would offset the header by however far
    the body happened to be scrolled — the control stays where you see it and
    answers presses somewhere else, which is the wrong-control bug this file
    already carries a scar about.
    */
    const localY = y - paddingTop
    const inHeader = headerRows.length > 0 && localY < headerH
    const contentY = inHeader ? localY : localY - headerH + scroll
    /*
    THE RAIL WINS, and it is tested before any row.

    Its whole purpose is to be a place you can press that is guaranteed NOT to
    be a control, so a row underneath it must never get the press. Nothing is
    laid out there — the body is measured `RAIL_W` narrower when the rail
    exists — but a widget that draws outside its width would otherwise take a
    scroll gesture and move a value instead.
    */
    if (scrollable && x >= railX && !inHeader) {
      if (kind === 'down') {
        scrolling = true
        scrollFrom = y
        // Grab the thumb where you pressed if you pressed ON it; otherwise
        // centre it there, so a press in the empty track is a page jump rather
        // than nothing happening.
        const thumbTop = Number(railThumb.getAttribute('y'))
        if (y < thumbTop || y > thumbTop + thumbH) {
          const travel = Math.max(1, viewport - thumbH)
          scroll = ((y - thumbH / 2 - railTop) / travel) * (total - viewport)
          applyScroll()
        }
        return
      }
      if (kind === 'move' && scrolling) {
        // Rail travel is SCALED: dragging the thumb its own length must move
        // the content by a page, not by that many pixels.
        const travel = Math.max(1, viewport - thumbH)
        scroll += ((y - scrollFrom) * (total - viewport)) / travel
        scrollFrom = y
        applyScroll()
        return
      }
      if (kind === 'up') {
        scrolling = false
        return
      }
    }
    const row = inHeader ? headerRowAt(localX, localY) : rowAt(localX, contentY)
    if (kind === 'down') {
      if (row) {
        /*
        A PRESS ELSEWHERE CLEARS THE LAST WIDGET'S INNER FOCUS.

        Without this a keyboard stayed up after you touched something that
        cannot use it — Tonio: "not disappearing if I click the lights checkbox".
        The panel is the only thing that knows focus moved, since each widget
        only ever hears about its own presses.

        `focusClear` already means "the host's focus left you", so this is the
        existing contract being honoured rather than a new one.
        */
        if (lastPressed != null && lastPressed !== row.w) {
          lastPressed.setActive?.(false)
        }
        lastPressed = row.w
        captured = { w: row.w, top: row.top, fromHeader: inHeader }
        row.w.handle?.('down', localX, contentY - row.top)
      } else if (scrollable && !inHeader) {
        // Empty space in the PINNED block is not a scroll handle — the body is
        // what scrolls, and dragging the chrome to move it reads as a bug.
        scrolling = true
        scrollFrom = y
      }
    } else if (kind === 'move' && captured) {
      const capturedY = captured.fromHeader ? localY : localY - headerH + scroll
      captured.w.handle?.('move', localX, capturedY - captured.top)
    } else if (kind === 'move' && scrolling) {
      scroll += scrollFrom - y
      scrollFrom = y
      applyScroll()
    } else {
      // hover (move without a press) or release
      if (kind === 'up' && captured) {
        // Released BEFORE the widget hears it: its click may rebuild this
        // panel, and a host holds a rebuild while `pressing` (see below).
        const c = captured
        captured = null
        const capturedY = c.fromHeader ? localY : localY - headerH + scroll
        c.w.handle?.('up', localX, capturedY - c.top)
      }
      scrolling = false
      setHover(row)
      if (hovered) hovered.w.handle?.('hover', localX, contentY - hovered.top)
    }
  }

  // Overlay: map native pointer coords → viewBox via the CTM, then route. Moves
  // route unconditionally so hover feedback works without a press.
  const toViewBox = (clientX: number, clientY: number) =>
    localPoint(root, clientX, clientY)
  /*
  STOP AT THE PANEL BOUNDARY.

  A panel can be nested inside another — a popup opened by `showPopup` IS a
  `panel3d` mounted in its opener's SVG — and it carries its own DOM listeners.
  Without this, one real pointer event is handled TWICE: once by the inner panel,
  then again when it bubbles to the outer one, which routes it straight back into
  the inner panel by coordinates.

  A tap survives being doubled. A HOLD does not: two `down`s restart the timer,
  so press-hold-drag on the spacebar never fired. Tonio: "press hold and drag on
  spacebar to move the selection doesn't work in the DOM ui, only in the 3D
  view" — and 3D worked precisely because a texture has no DOM events, leaving
  exactly one route.

  The event is the panel's own, on the panel's own element, so ending it here is
  what a nested interactive component should do.
  */
  root.addEventListener('pointerdown', (e) => {
    e.stopPropagation()
    const pe = e as PointerEvent
    const p = toViewBox(pe.clientX, pe.clientY)
    try {
      root.setPointerCapture(pe.pointerId)
    } catch {
      /* */
    }
    handlePointer('down', p.x, p.y)
  })
  root.addEventListener('pointermove', (e) => {
    e.stopPropagation()
    const pe = e as PointerEvent
    const p = toViewBox(pe.clientX, pe.clientY)
    handlePointer('move', p.x, p.y)
  })
  root.addEventListener('pointerup', (e) => {
    e.stopPropagation()
    const pe = e as PointerEvent
    const p = toViewBox(pe.clientX, pe.clientY)
    handlePointer('up', p.x, p.y)
    try {
      root.releasePointerCapture(pe.pointerId)
    } catch {
      /* */
    }
  })
  root.addEventListener('pointerleave', () => handlePointer('leave', 0, 0))
  if (scrollable) {
    root.addEventListener(
      'wheel',
      (e) => {
        const we = e as WheelEvent
        we.preventDefault()
        scroll += we.deltaY
        applyScroll()
      },
      { passive: false }
    )
  }

  /*
  A POPUP IN ONE CALL, because the friction was the whole problem.

  Everything needed already existed — `hostFor(i).showLayer` opens widgets in a
  real layer above the panel, unbounded, identically flat and in a headset — but
  it was reachable only from INSIDE a widget, and it wanted an anchor. So the
  cheap way to show a bit of extra information stayed "add some rows", and rows
  push the panel around.

  Tonio: *"It should be super easy to make popups so that it's the low friction
  way of presenting additional information."* And the reason it matters:
  *"Having one of these info panels push out the panel layout is a bad
  experience."*

  So: `panel.popup({ title }, ...widgets)`. A title row and a Close button are
  supplied, because a popup without a way out is a trap and every caller would
  otherwise write the same two lines.
  */
  ;(
    root as unknown as {
      popup: (
        config: {
          title?: string
          width?: number
          maxHeight?: number
          handleClose?: () => void
        },
        ...items: Widget3d[]
      ) => { close: () => void }
    }
  ).popup = (config, ...items) => {
    const host = hostFor(0)
    let handle: { close: () => void } | null = null
    const close = () => handle?.close()
    /*
    NO CLOSE BUTTON ROW. The mounting layer draws the chrome — a move glyph and
    a × in the popup's own title band, in BOTH presentations now — so a
    full-width button was a second way to do the same thing, and the less
    conventional one. Tonio: "The popups should just have the usual move and
    drag affordances rather than a giant close button and being stuck in place."
    */
    const rows: Widget3d[] = []
    if (config.title != null)
      rows.push(label3d({ text: config.title, bold: true }))
    rows.push(...items)
    handle = host.showLayer(
      {
        // Anchored to the panel's top edge rather than to a widget, because
        // nothing here is a widget: this is the PANEL showing something.
        anchor: { x: 0, y: 0, width: config.width ?? width, height: 0 },
        side: 'below',
        width: config.width,
        // A thing you might keep open while you use the panel behind it, so it
        // gets a title band you can drag it by and a × to dismiss it.
        chrome: true,
        /*
        CAPPED, so it SCROLLS rather than overflowing.

        Uncapped, a debug readout came out 572 units tall in a 360-unit demo
        and ran off the bottom of the page — taking its own Close button with
        it. A popup whose only exit is below the fold is a trap, and the fact
        that an outside press also dismisses it is not something a first-time
        user knows.
        */
        maxHeight: config.maxHeight ?? host.bounds.height,
        handleClose: config.handleClose,
      },
      ...rows
    )
    return { close }
  }

  // Exposed so an in-scene/VR host can feed picks (UV → viewBox coords) without
  // any DOM events — the whole point of staying coordinate-based.
  ;(root as unknown as { handlePointer: typeof handlePointer }).handlePointer =
    handlePointer
  // Scroll by a delta (viewBox units). For an in-scene/VR host to drive scroll
  // from a source that isn't a pointer — e.g. an XR thumbstick while pointing at
  // the panel. No-op when the content doesn't overflow.
  ;(root as unknown as { scrollBy: (dy: number) => void }).scrollBy = (
    dy: number
  ) => {
    if (!scrollable) return
    scroll += dy
    applyScroll()
  }
  // Read and set, so a host that rebuilds the panel can keep its place
  // (tosijs-3d#110: a rebuild came back at the top, the new row out of view).
  Object.defineProperty(root, 'scrollTop', {
    configurable: true,
    get: () => scroll,
    set: (v: number) => {
      scroll = Number(v) || 0
      applyScroll()
    },
  })
  // The least scroll that shows the band: nothing when it is already in view,
  // its top at the top when it is above, its bottom at the bottom when below
  // (its top, when it is taller than the viewport).
  ;(
    root as unknown as {
      reveal: (w: Widget3d, within?: { top: number; height: number }) => boolean
    }
  ).reveal = (w, within) => {
    const row = rows.find((r) => r.w === w)
    if (!row) return false
    const top = row.top + (within?.top ?? 0)
    const h = within?.height ?? row.height
    if (top < scroll) scroll = top
    else if (top + h > scroll + viewport)
      scroll = h > viewport ? top : top + h - viewport
    applyScroll()
    return true
  }
  // Whether the panel can scroll (content overflows) — so a host knows to route a
  // stick to it rather than to locomotion.
  ;(root as unknown as { scrollable: boolean }).scrollable = scrollable
  Object.defineProperty(root, 'pressing', {
    get: () => captured != null || scrolling,
  })
  // Where the grab bar is, in viewBox units. `0` when there is no grip, so a
  // host can test it without knowing whether it asked for one.
  ;(root as unknown as { gripHeight: number }).gripHeight = config.grip
    ? paddingTop
    : 0
  // Is this point the grip, i.e. the title bar less its close target? A host
  // that drags the panel asks this rather than testing `gripHeight` alone,
  // or a press on × would start a drag.
  ;(root as unknown as { gripHit: (x: number, y: number) => boolean }).gripHit =
    (x, y) =>
      !!config.grip &&
      y >= 0 &&
      y < paddingTop &&
      !(handleClose != null && bar != null && x >= bar.closeHitX)
  /*
  What the panel contains versus what it can show.
  
  The panel has always known this — `stackLayout` returns `total` — it simply
  never said, so every consumer sizing a panel was guessing at a number the
  panel could have told them. `fits: false` is the signal that something is
  hidden, which is otherwise indistinguishable from not being there.
  */
  ;(root as unknown as { measure: () => PanelFit }).measure = () =>
    panelFit(total, viewport)
  /*
  A POPUP IS JUST ANOTHER PANEL.

  Tonio: "popups need to just be actual panels." That is the same argument
  popup-surface already makes one level up — a popup is a new SURFACE, not a
  decoration drawn inside the old one — and it resolves the seam that had a
  real select, a colour picker and any in-panel menu blocked: `panel3d`
  returned a bare `<svg>` while `openPopup`/`openMenu` wanted a `Surface`, so a
  control inside a panel had nowhere to put its list.

  Nothing new is needed to build one. It is a `panel3d` with `height: 'fit'`
  (so it is exactly as tall as its options) placed by `placePopup` (which
  already flips and clamps). What this returns is the panel plus WHERE it goes;
  MOUNTING is deliberately the host's job, because that is the one part that
  genuinely differs: flat it is a positioned sibling, in a scene it is another
  plane. A popup that tried to live inside its opener would be clipped by the
  opener's own viewBox — which is precisely why it has to be its own panel.
  */
  ;(
    root as unknown as {
      openPopup: (
        config: {
          anchor: { x: number; y: number; width: number; height: number }
          side?: PopupSide
          width?: number
          maxHeight?: number
          /** Space the popup must fit inside. Defaults to the opener's own box. */
          bounds?: { width: number; height: number }
        },
        ...items: Widget3d[]
      ) => {
        el: SVGSVGElement
        x: number
        y: number
        side: PopupSide
        close: () => void
      }
    }
  ).openPopup = (config, ...items) => {
    const bounds = config.bounds ?? { width, height }
    const popup = panel3d(
      {
        width: config.width ?? Math.min(width, 260),
        height: 'fit',
        /*
        Never taller than the space it has to land in.
        
        Without this a popup with many options grows to fit them all and then
        cannot be placed anywhere — flipping does not help, because both sides
        are too small, so it just hangs off an edge. Capping at the bounds makes
        the overflow scroll instead, which it can do for free by being a real
        panel. Found by a test asserting the neither-side-fits case.
        */
        maxHeight: config.maxHeight ?? bounds.height,
      },
      ...items
    )
    const size = {
      width: Number(popup.getAttribute('width')),
      height: Number(popup.getAttribute('height')),
    }
    const placed = placePopup(
      config.anchor,
      size,
      bounds,
      config.side ?? 'below'
    )
    return {
      el: popup,
      x: placed.x,
      y: placed.y,
      side: placed.side,
      close: () => popup.remove(),
    }
  }

  /*
  How something ABOVE the panel volunteers a layer. `panelScene` calls this with
  a mounter that opens a real plane; without it `showLayer` degrades to
  `showPopup`, which is bounded — see `WidgetHost.showLayer`.
  */
  /**
   * Mount popups as positioned siblings in the DOM, above the panel.
   *
   * The flat counterpart of `panelScene`'s plane: outside the panel's `<svg>`,
   * so it is not cropped by the viewBox, and on top of it, so the separation is
   * real rather than drawn. Call it once with the element the panel sits in.
   *
   * Kept here rather than left to every demo because getting it wrong is
   * invisible — a popup inside the SVG merely looks *clipped*, which reads as a
   * layout bug rather than a mounting one.
   */
  /**
   * Mount popups as positioned siblings of the panel, above it.
   *
   * The flat counterpart of `panelScene`'s plane: outside the panel's `<svg>`, so
   * the viewBox cannot crop it, and on top of it, so the separation is real
   * rather than drawn.
   *
   * **Inserted as a SIBLING of the panel.** Whatever renders the panel renders
   * the node beside it — shadow root or document alike — which `parentElement`
   * cannot express, since a panel inside a `ShadowRoot` has no parent ELEMENT
   * and the layer was therefore never installed at all.
   *
   * **Positioned `absolute`**, so it scrolls with the page: a popup anchored to
   * a field must move with that field. (`fixed` needs no positioned ancestor and
   * is tempting for that reason, but it pins the keyboard to the viewport and a
   * single scroll detaches it from what it is typing into.) The container is
   * given `position: relative` when it is `static` — a small mutation, and the
   * price of anchoring to the right thing.
   *
   * The container argument is optional and only chooses where to INSERT; omit it
   * and the popup lands beside the panel, which is almost always right.
   */
  ;(
    root as unknown as {
      useDomLayer: (container?: HTMLElement) => () => void
    }
  ).useDomLayer = (container) => {
    StyleSheet('w3d-dom-layer', {
      '[data-w3d-dom-layer] [data-presentation="texture"]': {
        display: 'none',
      },
    })
    /*
    DECLARE THE CHROME, or the sheet leaves no room for it.

    `panelPopupSheet` reserves its top band only when some mounting host says it
    draws handles — the note on `chromeBand` is explicit that an unreserved band
    puts the glyphs on the content ("the move affordance overlaps the 'q'").
    This host draws them now, so it says so.
    */
    const domLayerHost: LayerHost = (sheet, config) => {
      const rect = root.getBoundingClientRect()
      /*
      A ZERO SCALE IS A ZERO-SIZE KEYBOARD, so never take one.

      `getBoundingClientRect()` returns all zeros for a panel that has not been
      laid out yet, is `display:none`, or sits in a hidden tab — and dividing by
      the panel's height then gives scale 0, so the popup is mounted at 0x0. It
      is present, correct, connected, and invisible, which is the hardest kind of
      wrong to diagnose (tosijs-ensemble reported exactly "the keyboard but it's
      zero size").

      Unscaled is the honest fallback: the sheet is authored in the panel's own
      units, so 1:1 is right whenever we cannot measure, and it is visible —
      which a reader can then correct.
      */
      const panelH = Math.max(1, Number(root.getAttribute('height')))
      const scale = rect.height > 0 ? rect.height / panelH : 1
      const holder = document.createElement('div')
      holder.setAttribute('data-w3d-dom-layer', '')
      holder.style.position = 'absolute'
      holder.style.zIndex = '10'
      // An explicit size, so a consumer stylesheet with fluid `svg { width:100% }`
      // cannot collapse the sheet against a shrink-to-fit holder.
      const w = Number(sheet.getAttribute('width')) || 360
      const h = Number(sheet.getAttribute('height')) || 200
      holder.style.width = `${w * scale}px`
      holder.style.height = `${h * scale}px`
      sheet.setAttribute('width', String(w * scale))
      sheet.setAttribute('height', String(h * scale))
      /*
      CHROME ON THE FLAT LAYER TOO — move and close, and a popup you can drag.

      This host used to draw none: the in-scene popup got handles from
      `popup-surface` and the DOM one got nothing, so flat a popup was stuck
      where it opened and its only exit was whatever button the caller had
      thought to include. Tonio: "The popups should just have the usual move and
      drag affordances rather than a giant close button and being stuck in
      place."

      Same geometry as the plane's, from `chromeLayout`, so the two
      presentations put the handles in the same place and a person who has
      learnt one has learnt the other.
      */
      const band = config.chromeBand ?? 0
      const chrome = chromeLayout(w, h, band / Math.max(1, h))
      let closeGlyph: Element | null = null
      if (band > 0 && chrome.barHeight > 0) {
        const mk = (name: string, at: { x: number; y: number; size: number }) =>
          iconGlyph(name, {
            color: TH.MUTED,
            size: at.size,
            x: at.x,
            y: at.y,
          })
        // The move glyph is PURELY an affordance — the drag below already
        // works anywhere that is not a widget. It is here because an
        // undiscoverable gesture is not a feature.
        if (chrome.move != null) sheet.appendChild(mk('move', chrome.move))
        closeGlyph = mk('close', chrome.close)
        sheet.appendChild(closeGlyph)
      }
      holder.appendChild(sheet)
      /*
      IN THE DOCUMENT BODY by default, not beside the panel. A panel usually
      sits in something that CLIPS (a doc page's demo frame, a scrolling
      sidebar), and a popup inserted beside it was cut off at that frame's
      edge however well it was placed in the viewport (#3478: the colour
      picker's popup lost its presets below the demo). In the body nothing
      clips it, and absolute page coordinates still scroll it with its field.
      A container passed to `useDomLayer` is still used as given.
      */
      // Only for a panel in the document itself: inside a shadow root the popup
      // stays beside its panel, in the same root (ensemble's topology).
      const inBody =
        container == null && root.isConnected && root.getRootNode() === document
      const parent =
        container ??
        (inBody ? document.body : (root.parentNode as ParentNode | null))
      if (parent == null) return { close: () => {} }
      /*
      `absolute` resolves against the nearest POSITIONED ancestor, so give the
      insertion point one if it lacks it — otherwise the popup is placed relative
      to the page and lands somewhere unrelated. A `ShadowRoot` has no style, so
      the host is the thing to reach for there.
      */
      const positioned = inBody
        ? null
        : container ??
          (parent instanceof ShadowRoot
            ? (parent.host as HTMLElement)
            : (parent as HTMLElement))
      if (
        positioned?.style != null &&
        getComputedStyle(positioned).position === 'static'
      ) {
        positioned.style.position = 'relative'
      }
      /*
      PLACE IT INSIDE THE VIEWPORT, not merely below the anchor.

      This used to set left/top straight from the anchor with no bounds check,
      so a field in a right-hand panel opened a keyboard 112px off the right
      edge — with its rightmost column of keys unreachable and, because the
      move/close chrome is scene-only, no way to get it back (#57).

      `placePopup` is the same pure flip/clamp the in-scene presentation uses:
      flip to the opposite side when the primary axis does not fit, clamp on
      the cross axis. Running it in VIEWPORT coordinates and converting once at
      the end keeps one placement rule for both presentations.
      */
      // In the body, offsets are page coordinates (the viewport plus scroll).
      const box = inBody
        ? { left: -window.scrollX, top: -window.scrollY }
        : positioned?.getBoundingClientRect?.() ?? { left: 0, top: 0 }
      const vw = window.innerWidth || 1024
      const vh = window.innerHeight || 768
      const placed = placePopup(
        {
          x: rect.left + config.anchor.x * scale,
          y: rect.top + config.anchor.y * scale,
          width: config.anchor.width * scale,
          height: config.anchor.height * scale,
        },
        { width: w * scale, height: h * scale },
        { width: vw, height: vh },
        config.side
      )
      // Offsets are relative to the positioned ancestor, so convert back.
      holder.style.left = `${placed.x - box.left}px`
      holder.style.top = `${placed.y - box.top}px`
      if (container != null) container.appendChild(holder)
      else if (inBody) {
        holder.style.zIndex = '1000'
        document.body.appendChild(holder)
      } else parent.insertBefore(holder, root.nextSibling)
      // In the body it no longer leaves with its panel's container, so it
      // leaves when its panel does.
      let gone: ReturnType<typeof setInterval> | undefined
      if (inBody) {
        gone = setInterval(() => {
          if (!root.isConnected) {
            clearInterval(gone)
            holder.remove()
          }
        }, 500)
      }

      /*
      DRAG IT. The clamp above stops the first frame being off-screen; this is
      what lets someone move it off the thing they are typing into.

      Tonio's framing on #57: the clipping "would actually be solved if the user
      could drag it around", which is better than any placement heuristic
      because only the person typing knows what the popup is covering.

      The in-scene presentation gets this from picking the move glyph, which
      does not exist flat — so flat gets the whole holder as the handle, minus
      the interactive parts. Dragging from a KEY would otherwise mean you could
      never type: a press that starts on a control belongs to the control.
      */
      let dragFrom: { x: number; y: number; left: number; top: number } | null =
        null
      const onDown = (e: PointerEvent) => {
        const target = e.target as Element | null
        /*
        A press on a WIDGET belongs to the widget; anything else drags.

        `closest('[data-w3d]')` alone is not the test — the panel root carries
        `data-w3d="panel"`, so it matches everywhere and the drag would never
        start. What matters is whether the press landed on a widget INSIDE the
        panel, so the root is excluded explicitly.
        */
        const hit = target?.closest?.('[data-w3d]') ?? null
        if (hit != null && hit !== sheet) return
        dragFrom = {
          x: e.clientX,
          y: e.clientY,
          left: parseFloat(holder.style.left) || 0,
          top: parseFloat(holder.style.top) || 0,
        }
        holder.setPointerCapture?.(e.pointerId)
      }
      const onMove = (e: PointerEvent) => {
        if (dragFrom == null) return
        holder.style.left = `${dragFrom.left + (e.clientX - dragFrom.x)}px`
        holder.style.top = `${dragFrom.top + (e.clientY - dragFrom.y)}px`
      }
      const onUp = () => {
        dragFrom = null
      }
      /*
      THE × CLOSES, and it has to be wired BEFORE the drag handler sees the
      press — otherwise pressing close drags the popup by its own close button,
      which is the most annoying possible outcome.

      A generous target rather than the glyph's own box: `chromeLayout` computes
      `closeHitX` separately for exactly this reason — a shrunken × on a
      controller ray is a miss waiting to happen, and a mouse deserves the same
      courtesy at the corner of a small popup.
      */
      if (closeGlyph != null) {
        const hit = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect'
        )
        hit.setAttribute('x', String(chrome.closeHitX))
        hit.setAttribute('y', '0')
        hit.setAttribute('width', String(Math.max(0, w - chrome.closeHitX)))
        hit.setAttribute('height', String(chrome.barHeight))
        hit.setAttribute('fill', 'transparent')
        hit.style.cursor = 'pointer'
        hit.addEventListener('pointerdown', (e) => {
          e.stopPropagation()
          config.handleClosed?.()
          holder.remove()
        })
        sheet.appendChild(hit)
      }
      /*
      CAPTURE PHASE, or the drag never starts.

      The sheet IS a `panel3d` root, and panel3d's own `pointerdown` listener
      calls `stopPropagation()` — deliberately, so a press on a panel does not
      reach the scene behind it. The consequence is that a bubble-phase listener
      on the HOLDER never hears anything: every press inside the popup was
      swallowed by the popup's own panel. Tonio: "It can't be dragged."

      Capture runs on the way DOWN, before the sheet sees the event, so the drag
      decision is made first — and `onDown` still bows out when the press landed
      on a widget, which is what keeps a slider inside a popup draggable as a
      slider rather than as the popup.
      */
      holder.style.touchAction = 'none'
      holder.style.cursor = 'move'
      holder.addEventListener('pointerdown', onDown, true)
      holder.addEventListener('pointermove', onMove, true)
      holder.addEventListener('pointerup', onUp, true)
      holder.addEventListener('pointercancel', onUp, true)

      return {
        close: () => {
          clearInterval(gone)
          holder.remove()
        },
      }
    }
    /*
    NOT `drawsChrome`. That flag means "always draws, so always reserve", which
    is the PLANE's situation. This host draws when it is given a band and
    nothing when it is not, which is what lets a flat menu stay bandless.
    */
    return (
      root as unknown as { addLayerHost: (fn: LayerHost) => () => void }
    ).addLayerHost(domLayerHost)
  }
  ;(
    root as unknown as { addLayerHost: (fn: LayerHost) => () => void }
  ).addLayerHost = (fn) => {
    const list = ((
      root as unknown as { __layerHosts?: LayerHost[] }
    ).__layerHosts ??= [])
    list.push(fn)
    return () => {
      const i = list.indexOf(fn)
      if (i >= 0) list.splice(i, 1)
    }
  }

  root.appendChild(bg)
  root.appendChild(clip)
  root.appendChild(clipWrap)
  // After the body, so the pinned block paints over anything that reaches it.
  if (headerWidgets.length > 0) root.appendChild(headerGroup)
  /*
  A GRIP, in the top padding strip that was already reserved.

  `paddingTop` exists so the first row clears the × close button, so the band is
  there whether or not anything is drawn in it. Drawing a grab bar costs no
  layout and makes the drag DISCOVERABLE, which a spatial panel badly needs —
  there is no cursor to change shape and no tooltip to hover.

  The host decides what a drag there means (`tosi-b3d` orbits the panel around
  its rig anchor); this only says where the handle is, via `gripHeight` on the
  element.
  */
  if (bar != null) {
    /*
    THE MOVE GLYPH, not a lozenge.

    The first version drew an iPhone-style grab bar, which reads as "drag me"
    to a phone user and as nothing in particular anywhere else — and this
    library already has an answer: `popup-surface` marks a draggable panel with
    `iconGlyph('move')`. Tonio: "The drag affordance looks a bit like an iphone
    lozenge, but we use the move cursor for panels in other cases."

    One vocabulary. A person who has learnt what the handle on a torn-off popup
    looks like should not have to learn a second one for a settings panel.

    `iconGlyph`, not `svgIcons`: this SVG is rasterised to a texture, where
    `currentColor` resolves against nothing and paints black.

    A TITLE BAR, laid out as a popup's: move at the left, the title beside
    it, close at the right. The band was already reserved and was empty
    space; Tonio: "missing its move and close widgets, and has a lot of waste
    space up top", and a named panel spent a body row on its own name.
    */
    root.append(
      ...titleBarParts(bar, config.title, handleClose != null, width, padding)
    )
  }
  root.appendChild(panelRim(width, height, w3dTheme.roundedRadius * 2))
  if (scrollable) {
    root.appendChild(railTrack)
    root.appendChild(railThumb)
    applyScroll() // seat the thumb at the top
  }
  /*
  DISPOSE THE ROWS WITH THE PANEL (#100). A consumer that builds its own
  panel and drops it on a rebuild calls this; a row bound to a tosijs box
  otherwise keeps the whole panel alive through tosijs's listener list.
  */
  ;(root as unknown as { dispose: () => void }).dispose = () => {
    for (const w of [...headerWidgets, ...widgets]) {
      try {
        w.dispose?.()
      } catch {
        // One row's dispose must not keep the others alive.
      }
    }
  }
  return root as unknown as Panel3d
}
